<template>
<div class="st-page">
  <Navigation />
  <div class="container" style="padding-top:20px;">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-3">
        <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
        <div>
          <h4 class="st-page-title"><i class="mdi mdi-shield-crown me-2"></i>Platform Admin</h4>
          <p style="color:#8BA4C4;font-size:12px;margin:0;">Simi Trac platform overview · monitor every school</p>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="st-btn st-btn-primary" @click="showOnboard = true"><i class="mdi mdi-domain-plus me-1"></i>Onboard School</button>
        <button class="st-btn st-btn-blue" @click="loadDashboard" :disabled="loading"><i class="mdi mdi-refresh me-1"></i>Refresh</button>
      </div>
    </div>

    <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
    <div v-else>

      <!-- KPI strip -->
      <div class="st-stat-row">
        <div class="st-stat"><div class="st-stat-num">{{ kpis.total_institutions }}</div><div class="st-stat-lbl">Institutions</div></div>
        <div class="st-stat"><div class="st-stat-num">{{ fNum(kpis.total_students) }}</div><div class="st-stat-lbl">Students</div></div>
        <div class="st-stat"><div class="st-stat-num">{{ fNum(kpis.total_staff) }}</div><div class="st-stat-lbl">Staff</div></div>
        <div class="st-stat"><div class="st-stat-num">{{ fNum(kpis.total_parents) }}</div><div class="st-stat-lbl">Parents</div></div>
        <div class="st-stat" style="border-color:rgba(245,158,11,.5);">
          <div class="st-stat-num" style="color:#F59E0B;">KSh {{ fNum(kpis.outstanding_total) }}</div>
          <div class="st-stat-lbl">Outstanding receivables · what schools owe us</div>
        </div>
        <div class="st-stat" style="border-color:rgba(16,185,129,.4);">
          <div class="st-stat-num" style="color:#10B981;">KSh {{ fNum(kpis.settled_this_month) }}</div>
          <div class="st-stat-lbl">Settled (this month) · KSh {{ fNum(kpis.settled_all_time) }} all-time</div>
        </div>
      </div>
      <div class="st-stat-row">
        <div class="st-stat"><div class="st-stat-num" style="color:#10B981;">KSh {{ fNum(kpis.revenue_this_month) }}</div><div class="st-stat-lbl">Accrued revenue (this month)</div></div>
        <div class="st-stat"><div class="st-stat-num" style="color:#4FC3F7;">KSh {{ fNum(kpis.revenue_all_time) }}</div><div class="st-stat-lbl">Accrued revenue (all-time)</div></div>
      </div>

      <div class="st-stat-row">
        <div class="st-stat"><div class="st-stat-num" style="color:#F59E0B;">{{ kpis.outstanding_parent_count }}</div><div class="st-stat-lbl">Outstanding parent invoices · KSh {{ fNum(kpis.outstanding_parent_amount) }}</div></div>
        <div class="st-stat"><div class="st-stat-num" style="color:#F59E0B;">{{ kpis.outstanding_school_count }}</div><div class="st-stat-lbl">Outstanding school invoices · KSh {{ fNum(kpis.outstanding_school_amount) }}</div></div>
        <div class="st-stat"><div class="st-stat-num">{{ fNum(kpis.pos_count_month) }}</div><div class="st-stat-lbl">POS sales (this month) · KSh {{ fNum(kpis.pos_volume_month) }}</div></div>
        <div class="st-stat"><div class="st-stat-num">{{ fNum(kpis.sms_sent_month) }}</div><div class="st-stat-lbl">SMS sent (this month)</div></div>
      </div>

      <!-- Tabs -->
      <div class="st-tabs">
        <button class="st-tab" :class="{active:tab==='institutions'}" @click="tab='institutions'">Institutions</button>
        <button class="st-tab" :class="{active:tab==='invoices'}" @click="onInvoicesTab">Invoices</button>
        <button class="st-tab" :class="{active:tab==='users'}" @click="onUsersTab">All Users</button>
        <button class="st-tab" :class="{active:tab==='cashflow'}" @click="onCashFlowTab">Cash Flow</button>
        <button class="st-tab" :class="{active:tab==='accounting'}" @click="onAccountingTab">Accounting</button>
        <button class="st-tab" :class="{active:tab==='revenue'}" @click="tab='revenue'">Revenue Activity</button>
        <button class="st-tab" :class="{active:tab==='breakdown'}" @click="tab='breakdown'">Revenue Breakdown</button>
        <button class="st-tab" :class="{active:tab==='team'}" @click="onTeamTab">Platform Team</button>
      </div>

      <!-- INSTITUTIONS TABLE -->
      <div v-if="tab==='institutions'" class="st-card">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="st-search-wrap"><i class="mdi mdi-magnify"></i><input class="st-search" v-model="search" placeholder="Search institutions..." /></div>
          <span style="color:#8BA4C4;font-size:13px;">{{ filteredInstitutions.length }} of {{ institutions.length }}</span>
        </div>
        <div style="overflow-x:auto;" v-if="filteredInstitutions.length">
          <table class="st-table">
            <thead><tr>
              <th>Institution</th><th>Mode</th><th>Students</th>
              <th>POS Rev (Mo)</th><th>Accrued</th><th>Paid</th><th>Owed</th>
              <th>Status</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="i in filteredInstitutions" :key="i.id" @click="openBilling(i)" style="cursor:pointer;">
                <td>
                  <div style="font-weight:600;color:#E8EDF4;">{{ i.name }}</div>
                  <div style="font-size:11px;color:#8BA4C4;">{{ i.type || '-' }} · {{ i.campuses }} campus{{ i.campuses === 1 ? '' : 'es' }} · {{ fNum(i.staff) }} staff · {{ fNum(i.parents) }} parents</div>
                </td>
                <td><span class="st-badge" :class="modeBadge(i.attendance_billing_mode, i.is_free, i.free_until)">{{ modeLabel(i.attendance_billing_mode, i.is_free, i.free_until) }}</span></td>
                <td>{{ fNum(i.students) }}</td>
                <td>KSh {{ fNum(i.pos_revenue_month) }}</td>
                <td>KSh {{ fNum(i.platform_revenue_total) }}</td>
                <td style="color:#10B981;">KSh {{ fNum(i.platform_settled_total) }}</td>
                <td :style="{color: Number(i.platform_outstanding) > 0 ? '#F59E0B' : '#8BA4C4', fontWeight: Number(i.platform_outstanding) > 0 ? 700 : 400}">
                  KSh {{ fNum(i.platform_outstanding) }}
                </td>
                <td><span class="st-badge" :class="i.is_active==1?'st-badge-success':'st-badge-danger'">{{ i.is_active==1?'Active':'Inactive' }}</span></td>
                <td>
                  <button class="st-btn st-btn-sm" v-if="Number(i.platform_outstanding) > 0" style="background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.4);" @click.stop="openSettlement(i)" title="Record payment received">
                    <i class="mdi mdi-cash-plus"></i>
                  </button>
                  <button class="st-btn st-btn-sm" :style="i.is_active==1 ? 'background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.4);' : 'background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.4);'" @click.stop="toggleInstitutionActive(i)" :title="i.is_active==1 ? 'Suspend (cuts access for not paying)' : 'Re-activate institution'">
                    <i :class="i.is_active==1 ? 'mdi mdi-power-off' : 'mdi mdi-power'"></i>
                  </button>
                  <button class="st-btn st-btn-sm st-btn-blue" @click.stop="openBilling(i)"><i class="mdi mdi-cog"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-shield-crown"></i>No institutions match</div>
      </div>

      <!-- ALL INVOICES (across institutions) -->
      <div v-if="tab==='invoices'" class="st-card">
        <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div class="st-search-wrap"><i class="mdi mdi-magnify"></i><input class="st-search" v-model="allInv.search" placeholder="Search invoice # or school..." @input="onAllInvSearch" /></div>
          <div class="d-flex gap-1 flex-wrap">
            <button class="st-pill" :class="{active: allInv.status === 'all'}" @click="setAllInvStatus('all')">All <span class="st-pill-num">{{ allInv.totals.count }}</span></button>
            <button class="st-pill" :class="{active: allInv.status === 'pending', warn: true}" @click="setAllInvStatus('pending')">Pending</button>
            <button class="st-pill" :class="{active: allInv.status === 'partial', warn: true}" @click="setAllInvStatus('partial')">Partial</button>
            <button class="st-pill" :class="{active: allInv.status === 'paid', ok: true}" @click="setAllInvStatus('paid')">Paid</button>
            <button class="st-pill" :class="{active: allInv.status === 'void', danger: true}" @click="setAllInvStatus('void')">Void</button>
          </div>
        </div>

        <!-- Aggregate summary strip — reflects the filtered set -->
        <div class="d-flex gap-2 mb-3 flex-wrap" v-if="allInv.totals.count > 0">
          <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:10px 14px;flex:1;min-width:140px;">
            <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">INVOICES SHOWN</div>
            <div style="color:#E8EDF4;font-size:18px;font-weight:800;">{{ allInv.totals.count }}</div>
          </div>
          <div style="background:#0A1628;border:1px solid rgba(46,134,222,.4);border-radius:8px;padding:10px 14px;flex:1;min-width:140px;">
            <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">TOTAL BILLED</div>
            <div style="color:#4FC3F7;font-size:18px;font-weight:800;">KSh {{ fNum(allInv.totals.amount) }}</div>
          </div>
          <div style="background:#0A1628;border:1px solid rgba(16,185,129,.4);border-radius:8px;padding:10px 14px;flex:1;min-width:140px;">
            <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">PAID</div>
            <div style="color:#10B981;font-size:18px;font-weight:800;">KSh {{ fNum(allInv.totals.paid) }}</div>
          </div>
          <div style="background:#0A1628;border:1px solid rgba(245,158,11,.4);border-radius:8px;padding:10px 14px;flex:1;min-width:140px;">
            <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">OUTSTANDING</div>
            <div :style="{color: Number(allInv.totals.outstanding) > 0 ? '#F59E0B' : '#8BA4C4', fontSize: '18px', fontWeight: 800}">KSh {{ fNum(allInv.totals.outstanding) }}</div>
          </div>
        </div>

        <div v-if="allInv.loading" class="st-spinner"><div class="spinner-border"></div></div>
        <div v-else-if="allInv.list.length" style="overflow-x:auto;">
          <table class="st-table">
            <thead><tr>
              <th>Invoice #</th><th>School</th><th>Period</th><th>Lines</th>
              <th>Amount</th><th>Paid</th><th>Outstanding</th><th>Status</th><th>Issued</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="i in allInv.list" :key="i.id" :style="i.status === 'void' ? 'opacity:.55;' : ''">
                <td style="font-weight:700;font-family:monospace;color:#4FC3F7;">{{ i.invoice_no }}</td>
                <td>
                  <div style="font-weight:600;">{{ i.institution_name }}</div>
                  <div v-if="i.issued_by_name" style="font-size:10px;color:#8BA4C4;">by {{ i.issued_by_name }}</div>
                </td>
                <td style="font-size:12px;">{{ i.period_start.substr(0, 10) }}<br/>→ {{ i.period_end.substr(0, 10) }}</td>
                <td>{{ i.line_count }}</td>
                <td style="font-weight:700;">KSh {{ fNum(i.amount) }}</td>
                <td :style="{color: Number(i.paid_amount) > 0 ? '#10B981' : '#8BA4C4'}">KSh {{ fNum(i.paid_amount) }}</td>
                <td :style="{color: Number(i.outstanding) > 0 ? '#F59E0B' : '#8BA4C4', fontWeight: Number(i.outstanding) > 0 ? 700 : 400}">KSh {{ fNum(i.outstanding) }}</td>
                <td><span class="st-badge" :class="invStatusBadge(i.status)">{{ i.status }}</span></td>
                <td style="font-size:11px;color:#8BA4C4;">{{ i.issued_at ? i.issued_at.substr(0, 10) : '—' }}</td>
                <td style="white-space:nowrap;">
                  <button class="st-btn st-btn-sm st-btn-blue me-1" @click="printInvoice(i)" title="Print or download as PDF">
                    <i class="mdi mdi-printer"></i>
                  </button>
                  <button class="st-btn st-btn-sm" @click="openBillingForInvoice(i)" title="Open this school's billing modal">
                    <i class="mdi mdi-cog"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-receipt-text"></i>No invoices match this filter.</div>
      </div>

      <!-- CASH FLOW -->
      <div v-if="tab==='cashflow'">
        <div class="st-stat-row" style="margin-bottom:14px;">
          <div class="st-stat" style="border-color:rgba(16,185,129,.4);"><div class="st-stat-num" style="color:#10B981;">KSh {{ fNum(cashFlow.kpis && cashFlow.kpis.total_month) }}</div><div class="st-stat-lbl">Total flow (this month)</div></div>
          <div class="st-stat" style="border-color:rgba(79,195,247,.4);"><div class="st-stat-num" style="color:#4FC3F7;">KSh {{ fNum(cashFlow.kpis && cashFlow.kpis.total_all) }}</div><div class="st-stat-lbl">Total flow (all-time)</div></div>
          <div class="st-stat"><div class="st-stat-num">KSh {{ fNum(cashFlow.kpis && cashFlow.kpis.mpesa_month) }}</div><div class="st-stat-lbl">M-Pesa (Mo) · {{ cashFlow.kpis && cashFlow.kpis.mpesa_count_month || 0 }} txns</div></div>
          <div class="st-stat"><div class="st-stat-num">KSh {{ fNum(cashFlow.kpis && cashFlow.kpis.cash_month) }}</div><div class="st-stat-lbl">Cash POS (Mo) · {{ cashFlow.kpis && cashFlow.kpis.cash_count_month || 0 }} sales</div></div>
        </div>

        <div class="row g-3" style="margin-bottom:14px;">
          <div class="col-lg-6">
            <div class="st-card">
              <div style="color:#E8EDF4;font-weight:700;font-size:14px;margin-bottom:10px;">This month — by purpose</div>
              <div v-if="cashFlow.by_purpose && cashFlow.by_purpose.length">
                <div v-for="r in cashFlow.by_purpose" :key="r.purpose" style="display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:#0A1628;border-radius:8px;margin-bottom:6px;border-left:3px solid #4FC3F7;">
                  <div>
                    <div style="color:#E8EDF4;font-weight:600;text-transform:capitalize;">{{ (r.purpose || '').replace('_', ' ') }}</div>
                    <div style="color:#8BA4C4;font-size:11px;">{{ r.cnt }} payment{{ r.cnt == 1 ? '' : 's' }}</div>
                  </div>
                  <div style="color:#10B981;font-weight:700;">KSh {{ fNum(r.total) }}</div>
                </div>
              </div>
              <div v-else class="st-empty"><i class="mdi mdi-cash-multiple"></i>No payments yet</div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="st-card">
              <div style="color:#E8EDF4;font-weight:700;font-size:14px;margin-bottom:10px;">This month — by method</div>
              <div v-if="cashFlow.by_method && cashFlow.by_method.length">
                <div v-for="r in cashFlow.by_method" :key="r.payment_method" style="display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:#0A1628;border-radius:8px;margin-bottom:6px;border-left:3px solid #10B981;">
                  <div>
                    <div style="color:#E8EDF4;font-weight:600;text-transform:capitalize;">{{ (r.payment_method || '').replace('mpesa_', 'M-Pesa ').replace('_', ' ') }}</div>
                    <div style="color:#8BA4C4;font-size:11px;">{{ r.cnt }} payment{{ r.cnt == 1 ? '' : 's' }}</div>
                  </div>
                  <div style="color:#10B981;font-weight:700;">KSh {{ fNum(r.total) }}</div>
                </div>
              </div>
              <div v-else class="st-empty"><i class="mdi mdi-cash-multiple"></i>No payments yet</div>
            </div>
          </div>
        </div>

        <div class="st-card" style="margin-bottom:14px;">
          <div style="color:#E8EDF4;font-weight:700;font-size:14px;margin-bottom:10px;">Per-school flow</div>
          <div v-if="cashFlow.per_school && cashFlow.per_school.length" style="overflow-x:auto;">
            <table class="st-table">
              <thead><tr><th>School</th><th>Wallet top-ups (Mo)</th><th>Fees (Mo)</th><th>M-Pesa (Mo)</th><th>Cash POS (Mo)</th><th>Total (Mo)</th><th>All-time</th></tr></thead>
              <tbody>
                <tr v-for="s in cashFlow.per_school" :key="s.id">
                  <td style="font-weight:600;">{{ s.name }}</td>
                  <td>KSh {{ fNum(s.wallet_topups_month) }}</td>
                  <td>KSh {{ fNum(s.fees_month) }}</td>
                  <td>KSh {{ fNum(s.mpesa_month) }}</td>
                  <td>KSh {{ fNum(s.cash_month) }}</td>
                  <td style="color:#10B981;font-weight:700;">KSh {{ fNum(Number(s.mpesa_month) + Number(s.cash_month)) }}</td>
                  <td>KSh {{ fNum(Number(s.mpesa_all) + Number(s.cash_all)) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="st-empty"><i class="mdi mdi-school"></i>No schools yet</div>
        </div>

        <div class="st-card">
          <div style="color:#E8EDF4;font-weight:700;font-size:14px;margin-bottom:10px;">Recent payments (latest 25)</div>
          <div v-if="cashFlow.recent && cashFlow.recent.length" style="overflow-x:auto;">
            <table class="st-table">
              <thead><tr><th>When</th><th>School</th><th>Payer</th><th>Phone</th><th>Purpose</th><th>Method</th><th>Amount</th><th>Ref</th></tr></thead>
              <tbody>
                <tr v-for="p in cashFlow.recent" :key="p.id">
                  <td style="white-space:nowrap;color:#8BA4C4;font-size:12px;">{{ formatDate(p.completed_at) }}</td>
                  <td>{{ p.institution_name || '—' }}</td>
                  <td>{{ p.payer_name || '—' }}</td>
                  <td>{{ p.payer_phone || '—' }}</td>
                  <td><span class="st-badge st-badge-info" style="text-transform:capitalize;">{{ (p.purpose || '').replace('_', ' ') }}</span></td>
                  <td style="text-transform:capitalize;font-size:12px;color:#8BA4C4;">{{ (p.payment_method || '').replace('mpesa_', 'M-Pesa ').replace('_', ' ') }}</td>
                  <td style="color:#10B981;font-weight:700;">KSh {{ fNum(p.amount) }}</td>
                  <td style="font-size:11px;color:#8BA4C4;font-family:monospace;">{{ p.provider_ref || p.payment_ref }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="st-empty"><i class="mdi mdi-cash-clock"></i>No payments yet</div>
        </div>
      </div>

      <!-- ACCOUNTING -->
      <div v-if="tab==='accounting'">
        <div class="st-stat-row" style="margin-bottom:14px;">
          <div class="st-stat" style="border-color:rgba(245,158,11,.4);"><div class="st-stat-num" style="color:#F59E0B;">KSh {{ fNum(acct.invoice_totals && acct.invoice_totals.pending) }}</div><div class="st-stat-lbl">Outstanding (pending invoices · {{ acct.invoice_totals && acct.invoice_totals.pending_count || 0 }})</div></div>
          <div class="st-stat" style="border-color:rgba(239,68,68,.4);"><div class="st-stat-num" style="color:#EF4444;">KSh {{ fNum(acct.invoice_totals && acct.invoice_totals.overdue) }}</div><div class="st-stat-lbl">Overdue</div></div>
          <div class="st-stat" style="border-color:rgba(16,185,129,.4);"><div class="st-stat-num" style="color:#10B981;">KSh {{ fNum(acct.collected_this_month) }}</div><div class="st-stat-lbl">Collected (this month)</div></div>
          <div class="st-stat" style="border-color:rgba(79,195,247,.4);"><div class="st-stat-num" style="color:#4FC3F7;">KSh {{ fNum(acct.invoice_totals && acct.invoice_totals.collected) }}</div><div class="st-stat-lbl">Collected (all-time · {{ acct.invoice_totals && acct.invoice_totals.paid_count || 0 }} invoices)</div></div>
          <div class="st-stat"><div class="st-stat-num">KSh {{ fNum(acct.unsettled_revenue) }}</div><div class="st-stat-lbl">Unsettled platform revenue</div></div>
        </div>

        <div class="st-card" style="margin-bottom:14px;">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <div style="color:#E8EDF4;font-weight:700;font-size:15px;">Month-end runner</div>
              <div style="color:#8BA4C4;font-size:12px;">Generate per-student-per-term invoices for every active institution at once. Skips schools in free trial.</div>
            </div>
            <button class="st-btn st-btn-primary" @click="runMonthEnd" :disabled="acctRunning">
              <span v-if="acctRunning" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="mdi mdi-receipt-text-plus me-1"></i>{{ acctRunning ? 'Running...' : 'Run Month-End' }}
            </button>
          </div>
          <div v-if="monthEndResult" class="st-alert st-alert-success mt-3">{{ monthEndResult }}</div>
        </div>

        <div class="st-card" style="margin-bottom:14px;">
          <div style="color:#E8EDF4;font-weight:700;font-size:14px;margin-bottom:10px;">Settlement status by school</div>
          <div v-if="acct.per_school && acct.per_school.length" style="overflow-x:auto;">
            <table class="st-table">
              <thead><tr><th>School</th><th>Owed</th><th>Paid (all-time)</th><th>Unsettled rev.</th><th>Last payment</th></tr></thead>
              <tbody>
                <tr v-for="s in acct.per_school" :key="s.id">
                  <td style="font-weight:600;">{{ s.name }}</td>
                  <td :style="{color: Number(s.owed) > 0 ? '#F59E0B' : '#8BA4C4', fontWeight: Number(s.owed) > 0 ? 700 : 400}">KSh {{ fNum(s.owed) }}</td>
                  <td style="color:#10B981;">KSh {{ fNum(s.paid_total) }}</td>
                  <td>KSh {{ fNum(s.unsettled_revenue) }}</td>
                  <td style="color:#8BA4C4;font-size:12px;">{{ s.last_paid_at ? formatDate(s.last_paid_at) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="st-card">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div style="color:#E8EDF4;font-weight:700;font-size:14px;">School invoices</div>
            <div class="d-flex gap-2 align-items-center">
              <select class="st-select" style="width:auto;font-size:12px;padding:6px 12px;" v-model="invFilter" @change="loadInvoices">
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="">All</option>
              </select>
            </div>
          </div>
          <div v-if="invoices.length" style="overflow-x:auto;">
            <table class="st-table">
              <thead><tr><th>Invoice #</th><th>School</th><th>Term</th><th>Description</th><th>Amount</th><th>Paid</th><th>Status</th><th>Due</th><th></th></tr></thead>
              <tbody>
                <tr v-for="inv in invoices" :key="inv.id">
                  <td style="font-weight:700;">{{ inv.invoice_number }}</td>
                  <td>{{ inv.institution_name }}</td>
                  <td>{{ inv.term_name || '—' }}</td>
                  <td style="font-size:12px;color:#8BA4C4;max-width:280px;">{{ inv.description }}</td>
                  <td>KSh {{ fNum(inv.amount) }}</td>
                  <td>{{ inv.paid_amount ? 'KSh ' + fNum(inv.paid_amount) : '—' }}</td>
                  <td><span class="st-badge" :class="invoiceBadge(inv.status)">{{ inv.status }}</span></td>
                  <td style="color:#8BA4C4;font-size:12px;">{{ inv.due_date || '—' }}</td>
                  <td>
                    <button v-if="inv.status === 'pending' || inv.status === 'overdue'" class="st-btn st-btn-sm st-btn-green" @click="openSettle(inv)">Settle</button>
                    <span v-else-if="inv.status === 'paid'" style="color:#10B981;"><i class="mdi mdi-check-circle"></i></span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="st-empty"><i class="mdi mdi-receipt"></i>No invoices match this filter</div>
        </div>
      </div>

      <!-- RECENT REVENUE ACTIVITY -->
      <div v-if="tab==='revenue'" class="st-card">
        <div style="color:#E8EDF4;font-weight:700;font-size:15px;margin-bottom:14px;">Latest 25 platform revenue events</div>
        <div v-if="recentRevenue.length" style="overflow-x:auto;">
          <table class="st-table">
            <thead><tr><th>When</th><th>Institution</th><th>Source</th><th>Amount</th><th>Notes</th></tr></thead>
            <tbody>
              <tr v-for="r in recentRevenue" :key="r.id">
                <td style="white-space:nowrap;color:#8BA4C4;font-size:12px;">{{ formatDate(r.created_at) }}</td>
                <td style="font-weight:600;">{{ r.institution_name || '-' }}</td>
                <td><span class="st-badge st-badge-info">{{ revenueLabel(r.source_type) }}</span></td>
                <td style="color:#10B981;font-weight:700;">KSh {{ fNum(r.amount) }}</td>
                <td style="color:#8BA4C4;font-size:12px;">{{ r.notes || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-cash-multiple"></i>No revenue events yet</div>
      </div>

      <!-- ALL USERS -->
      <div v-if="tab==='users'" class="st-card">
        <div class="row g-2 mb-3">
          <div class="col-md-4">
            <label class="st-label">Filter by school</label>
            <select class="st-select" v-model="userFilters.institutionId" @change="loadAllUsers">
              <option value="">All schools</option>
              <option v-for="i in institutions" :key="i.id" :value="i.id">{{ i.name }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="st-label">User type</label>
            <select class="st-select" v-model="userFilters.userType" @change="loadAllUsers">
              <option value="">All</option>
              <option value="staff">Staff</option>
              <option value="parent">Parents</option>
              <option value="student">Students</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="st-label">Status</label>
            <select class="st-select" v-model="userFilters.status" @change="loadAllUsers">
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="st-btn st-btn-blue w-100" @click="loadAllUsers" :disabled="usersLoading">
              <i class="mdi mdi-refresh me-1"></i>{{ usersLoading ? 'Loading...' : 'Apply' }}
            </button>
          </div>
        </div>
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="st-search-wrap"><i class="mdi mdi-magnify"></i><input class="st-search" v-model="userFilters.search" placeholder="Search name / email / phone..." @keyup.enter="loadAllUsers" /></div>
          <span style="color:#8BA4C4;font-size:13px;">{{ allUsers.length }} users</span>
        </div>
        <div v-if="allUsers.length" style="overflow-x:auto;">
          <table class="st-table">
            <thead><tr><th>Name</th><th>School</th><th>Type</th><th>Email</th><th>Phone</th><th>Category</th><th>Last login</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <tr v-for="u in allUsers" :key="u.id">
                <td style="font-weight:600;">{{ u.name }}</td>
                <td>{{ u.institution_name || '—' }}</td>
                <td><span class="st-badge" :class="userTypeBadge(u.role)">{{ userTypeLabel(u.role) }}</span></td>
                <td>{{ u.email || '—' }}</td>
                <td>{{ u.phone || '—' }}</td>
                <td>
                  <span v-if="u.permission_group_name" style="font-size:11px;color:#4FC3F7;">{{ u.permission_group_name }}</span>
                  <span v-else style="color:#8BA4C4;font-size:11px;">—</span>
                </td>
                <td style="color:#8BA4C4;font-size:12px;">{{ u.last_login_at ? formatDate(u.last_login_at) : '—' }}</td>
                <td><span class="st-badge" :class="u.is_active==1?'st-badge-success':'st-badge-danger'">{{ u.is_active==1?'Active':'Inactive' }}</span></td>
                <td>
                  <button class="st-btn st-btn-sm st-btn-blue" @click="resetUserPassword(u)" title="Reset password and notify the user"><i class="mdi mdi-key"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="!usersLoading" class="st-empty"><i class="mdi mdi-account-group"></i>No users match these filters</div>
        <div v-else class="st-spinner"><div class="spinner-border"></div></div>
      </div>

      <!-- PLATFORM TEAM -->
      <div v-if="tab==='team'" class="st-card">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div style="color:#E8EDF4;font-weight:700;font-size:15px;">Platform Owners</div>
            <div style="color:#8BA4C4;font-size:12px;">Anyone here has full platform access. Add new teammates with care.</div>
          </div>
          <button class="st-btn st-btn-primary" @click="showAddOwner = true"><i class="mdi mdi-account-plus me-1"></i>Add Platform Owner</button>
        </div>
        <div v-if="platformOwners.length" style="overflow-x:auto;">
          <table class="st-table">
            <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Status</th><th>Last login</th><th>Created</th><th></th></tr></thead>
            <tbody>
              <tr v-for="o in platformOwners" :key="o.id">
                <td style="font-weight:600;">{{ o.name }}</td>
                <td>{{ o.email || '-' }}</td>
                <td>{{ o.phone || '-' }}</td>
                <td><span class="st-badge" :class="o.is_active==1?'st-badge-success':'st-badge-danger'">{{ o.is_active==1?'Active':'Revoked' }}</span></td>
                <td style="color:#8BA4C4;font-size:12px;">{{ o.last_login_at ? formatDate(o.last_login_at) : '—' }}</td>
                <td style="color:#8BA4C4;font-size:12px;">{{ formatDate(o.created_at) }}</td>
                <td>
                  <button v-if="o.is_active==1" class="st-btn st-btn-sm" style="background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);" @click="revokeOwner(o)" title="Revoke access">
                    <i class="mdi mdi-account-cancel"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-shield-crown"></i>Loading...</div>
      </div>

      <!-- REVENUE BREAKDOWN -->
      <div v-if="tab==='breakdown'" class="st-card">
        <div style="color:#E8EDF4;font-weight:700;font-size:15px;margin-bottom:14px;">Revenue this month — by source</div>
        <div v-if="kpis.revenue_by_type_month && kpis.revenue_by_type_month.length">
          <div v-for="r in kpis.revenue_by_type_month" :key="r.source_type" style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:#0A1628;border-radius:8px;margin-bottom:8px;border-left:3px solid #4FC3F7;">
            <div>
              <div style="color:#E8EDF4;font-weight:600;">{{ revenueLabel(r.source_type) }}</div>
              <div style="color:#8BA4C4;font-size:11px;">{{ revenueDescription(r.source_type) }}</div>
            </div>
            <div style="color:#10B981;font-weight:800;font-size:18px;">KSh {{ fNum(r.total) }}</div>
          </div>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-chart-pie"></i>No revenue this month yet</div>
      </div>
    </div>

    <!-- SETTLE INVOICE MODAL -->
    <div class="st-modal-overlay" v-if="showSettle" @click.self="showSettle = false">
      <div class="st-modal" style="max-width:520px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-check-decagram me-2"></i>Settle Invoice</span>
          <button class="st-modal-close" @click="showSettle = false">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="settleError">{{ settleError }}</div>
        <div v-if="settleInv" style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:14px;margin-bottom:14px;">
          <div style="color:#E8EDF4;font-weight:700;">{{ settleInv.invoice_number }}</div>
          <div style="color:#8BA4C4;font-size:12px;margin-top:4px;">{{ settleInv.institution_name }} · {{ settleInv.term_name || '—' }}</div>
          <div style="color:#8BA4C4;font-size:12px;">{{ settleInv.description }}</div>
          <div style="color:#10B981;font-weight:800;margin-top:6px;">Invoiced: KSh {{ fNum(settleInv.amount) }}</div>
        </div>

        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <label class="st-label">Payment method</label>
            <select class="st-select" v-model="settleForm.paymentMethod">
              <option value="mpesa">M-Pesa</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="st-label">Amount paid (KSh)</label>
            <input type="number" min="0" step="100" class="st-input" v-model.number="settleForm.paidAmount" />
          </div>
        </div>
        <div class="mb-3">
          <label class="st-label">M-Pesa code / Reference</label>
          <input class="st-input" v-model="settleForm.mpesaCode" placeholder="e.g. RJ7XK4P9MB" />
        </div>
        <div class="mb-3">
          <label class="st-label">Notes</label>
          <input class="st-input" v-model="settleForm.notes" placeholder="Optional" />
        </div>
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;color:#E8EDF4;font-size:13px;margin-bottom:14px;">
          <input type="checkbox" v-model="settleForm.settleAllPending" /> Also mark this school's other unsettled commission/revenue as settled
        </label>
        <div class="d-flex gap-2">
          <button class="btn st-btn-primary flex-fill" @click="confirmSettle" :disabled="settleSaving">
            <span v-if="settleSaving" class="spinner-border spinner-border-sm me-1"></span>{{ settleSaving ? 'Settling...' : 'Mark Paid' }}
          </button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="showSettle = false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ONBOARD SCHOOL MODAL -->
    <div class="st-modal-overlay" v-if="showOnboard" @click.self="closeOnboard">
      <div class="st-modal" style="max-width:620px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-domain-plus me-2"></i>Onboard New School</span>
          <button class="st-modal-close" @click="closeOnboard">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="onboardError">{{ onboardError }}</div>

        <div v-if="onboardSuccess" class="text-center">
          <div style="width:64px;height:64px;border-radius:50%;background:rgba(16,185,129,.15);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
            <i class="mdi mdi-check-circle" style="color:#10B981;font-size:36px;"></i>
          </div>
          <h5 style="color:#E8EDF4;font-weight:700;margin:0 0 8px;">{{ onboardForm.name }} is live</h5>
          <p style="color:#8BA4C4;font-size:13px;">Super-admin credentials sent via SMS{{ onboardForm.email ? ' and email' : '' }}.</p>
          <div v-if="onboardAppliedPresets.length" style="background:rgba(16,185,129,.08);border:1px solid rgba(16,185,129,.25);border-radius:8px;padding:10px 12px;margin:10px 0;text-align:left;font-size:12px;color:#10B981;">
            <i class="mdi mdi-check-circle me-1"></i>Auto-applied grading presets: <strong>{{ onboardAppliedPresets.join(', ') }}</strong>
          </div>
          <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:14px;margin:14px 0;text-align:left;">
            <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#8BA4C4;font-size:12px;">Super Admin</span><span style="color:#E8EDF4;font-weight:600;">{{ onboardForm.adminName }}</span></div>
            <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#8BA4C4;font-size:12px;">Login</span><span style="color:#E8EDF4;font-weight:600;">{{ onboardForm.email || onboardForm.phone }}</span></div>
            <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#8BA4C4;font-size:12px;">Password</span><span style="color:#F59E0B;font-weight:700;">{{ onboardTempPassword }}</span></div>
          </div>
          <div class="d-flex gap-2 mt-3">
            <button class="btn st-btn-primary flex-fill" @click="resetOnboard">+ Onboard Another</button>
            <button class="btn flex-fill" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeOnboard">Close</button>
          </div>
        </div>

        <div v-else>
          <div style="font-size:11px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin-bottom:8px;">School details</div>
          <div class="row g-2 mb-3">
            <div class="col-md-12"><label class="st-label">School Name *</label><input class="st-input" v-model="onboardForm.name" placeholder="e.g. Riverside Academy" /></div>
          </div>
          <div class="row g-2 mb-3">
            <div class="col-md-6"><label class="st-label">Education Type *</label>
              <select class="st-select" v-model="onboardForm.educationType">
                <option value="pre_primary">Pre-Primary (PP1, PP2)</option>
                <option value="primary">Primary School (Grade 1–6)</option>
                <option value="junior_school">Junior School (Grade 7–9)</option>
                <option value="secondary">Secondary School / High School</option>
                <option value="mixed_school">Mixed School (covers multiple K-12 levels)</option>
                <option value="tvet">TVET / Technical Institute</option>
                <option value="college">College (Diploma / Certificate)</option>
                <option value="university">University</option>
                <option value="mixed_tertiary">Mixed Tertiary</option>
              </select>
              <small style="color:#8BA4C4;font-size:11px;">Drives default grading presets + UI vocabulary.</small>
            </div>
            <div class="col-md-6"><label class="st-label">Legacy Type</label>
              <select class="st-select" v-model="onboardForm.type">
                <option value="primary">Primary School</option>
                <option value="secondary">Secondary School</option>
                <option value="university">University</option>
                <option value="college">College</option>
                <option value="boarding">Boarding School</option>
                <option value="mixed">Mixed</option>
              </select>
            </div>
          </div>
          <div class="row g-2 mb-3">
            <div class="col-md-6"><label class="st-label">School Email *</label><input type="email" class="st-input" v-model="onboardForm.email" placeholder="info@school.ac.ke" /></div>
            <div class="col-md-6"><label class="st-label">City</label><input class="st-input" v-model="onboardForm.city" placeholder="e.g. Nairobi" /></div>
          </div>

          <div style="font-size:11px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin:16px 0 8px;">Super admin (first login)</div>
          <div class="row g-2 mb-3">
            <div class="col-md-6"><label class="st-label">Admin Full Name *</label><input class="st-input" v-model="onboardForm.adminName" placeholder="e.g. Mary Kamau" /></div>
            <div class="col-md-6"><label class="st-label">Admin Phone *</label><input class="st-input" v-model="onboardForm.phone" placeholder="0712345678" /></div>
          </div>
          <div class="mb-3">
            <label class="st-label">Initial Password <small style="color:#8BA4C4;font-weight:400;">(auto-generated if empty)</small></label>
            <input type="text" class="st-input" v-model="onboardForm.password" placeholder="Leave empty to auto-generate" />
          </div>

          <div style="font-size:11px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin:16px 0 8px;">Billing</div>
          <div class="row g-2 mb-3">
            <div class="col-md-7">
              <label class="st-label">Attendance billing mode</label>
              <select class="st-select" v-model="onboardForm.billingMode">
                <option value="direct_parent">Direct to parent</option>
                <option value="bundled_in_fees">Bundled in school fees</option>
                <option value="free_trial">Free trial</option>
              </select>
            </div>
            <div class="col-md-5">
              <label class="st-label">Initial status</label>
              <select class="st-select" v-model.number="onboardForm.isFree">
                <option :value="1">Free / trial</option>
                <option :value="0">Paid from day 1</option>
              </select>
            </div>
          </div>

          <div style="font-size:12px;color:#10B981;background:rgba(16,185,129,.08);border:1px solid rgba(16,185,129,.2);border-radius:8px;padding:10px 12px;margin-bottom:14px;">
            <i class="mdi mdi-information me-1"></i>The school will be created with 500 free SMS credits, all 9 system permission categories seeded, and the super admin's account verified — they can log in immediately. No OTP required.
          </div>

          <div class="d-flex gap-2">
            <button class="btn st-btn-primary flex-fill" @click="onboardSchool" :disabled="onboardSaving">
              <span v-if="onboardSaving" class="spinner-border spinner-border-sm me-1"></span>
              {{ onboardSaving ? 'Onboarding...' : 'Onboard School' }}
            </button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeOnboard">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ADD PLATFORM OWNER MODAL -->
    <div class="st-modal-overlay" v-if="showAddOwner" @click.self="closeAddOwner">
      <div class="st-modal" style="max-width:520px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-shield-crown me-2"></i>Add Platform Owner</span>
          <button class="st-modal-close" @click="closeAddOwner">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="addOwnerError">{{ addOwnerError }}</div>

        <!-- Success state with credentials -->
        <div v-if="addOwnerSuccess" class="text-center">
          <div style="width:64px;height:64px;border-radius:50%;background:rgba(16,185,129,.15);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
            <i class="mdi mdi-check-circle" style="color:#10B981;font-size:36px;"></i>
          </div>
          <h5 style="color:#E8EDF4;font-weight:700;margin:0 0 8px;">Platform Owner created</h5>
          <p style="color:#8BA4C4;font-size:13px;">Credentials sent via {{ addOwnerSentVia }}.</p>
          <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:14px;margin:14px 0;text-align:left;">
            <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#8BA4C4;font-size:12px;">Name</span><span style="color:#E8EDF4;font-weight:600;">{{ addOwnerForm.name }}</span></div>
            <div v-if="addOwnerForm.email" style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#8BA4C4;font-size:12px;">Email</span><span style="color:#E8EDF4;font-weight:600;">{{ addOwnerForm.email }}</span></div>
            <div v-if="addOwnerForm.phone" style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#8BA4C4;font-size:12px;">Phone</span><span style="color:#E8EDF4;font-weight:600;">{{ addOwnerForm.phone }}</span></div>
            <div style="display:flex;justify-content:space-between;padding:4px 0;"><span style="color:#8BA4C4;font-size:12px;">Password</span><span style="color:#F59E0B;font-weight:700;">{{ addOwnerTempPassword }}</span></div>
          </div>
          <div class="d-flex gap-2 mt-3">
            <button class="btn st-btn-primary flex-fill" @click="resetAddOwner">+ Add Another</button>
            <button class="btn flex-fill" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeAddOwner">Close</button>
          </div>
        </div>

        <!-- Form state -->
        <div v-else>
          <div class="mb-3"><label class="st-label">Full Name *</label><input class="st-input" v-model="addOwnerForm.name" placeholder="e.g. Jane Wanjiru" /></div>
          <div class="row g-2 mb-3">
            <div class="col-7"><label class="st-label">Email</label><input type="email" class="st-input" v-model="addOwnerForm.email" placeholder="jane@simitrac.co.ke" /></div>
            <div class="col-5"><label class="st-label">Phone</label><input class="st-input" v-model="addOwnerForm.phone" placeholder="0712345678" /></div>
          </div>
          <div class="mb-3">
            <label class="st-label">Password <small style="color:#8BA4C4;font-weight:400;">(auto-generated if empty)</small></label>
            <input type="text" class="st-input" v-model="addOwnerForm.password" placeholder="Leave empty to auto-generate" />
          </div>
          <div style="font-size:12px;color:#F59E0B;background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.2);border-radius:8px;padding:10px 12px;margin-bottom:14px;">
            <i class="mdi mdi-alert me-1"></i>Platform Owners can see every school, edit billing rates, and manage the entire platform. Only add trusted teammates.
          </div>
          <div class="d-flex gap-2">
            <button class="btn st-btn-primary flex-fill" @click="addOwner" :disabled="addOwnerSaving">
              <span v-if="addOwnerSaving" class="spinner-border spinner-border-sm me-1"></span>
              {{ addOwnerSaving ? 'Creating...' : 'Create Platform Owner' }}
            </button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeAddOwner">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- BILLING MODAL -->
    <div class="st-modal-overlay" v-if="showBilling" @click.self="closeBilling">
      <div class="st-modal" style="max-width:760px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-cash-multiple me-2"></i>Billing — {{ active.institution && active.institution.name }}</span>
          <button class="st-modal-close" @click="closeBilling">&times;</button>
        </div>
        <div class="st-alert st-alert-success" v-if="billingMsg">{{ billingMsg }}</div>
        <div class="st-alert st-alert-danger" v-if="billingError">{{ billingError }}</div>

        <div v-if="billingLoading" class="st-spinner"><div class="spinner-border"></div></div>
        <div v-else>
          <div class="st-stat-row" style="margin-bottom:16px;">
            <div class="st-stat"><div class="st-stat-num">{{ active.stats && active.stats.active_students || 0 }}</div><div class="st-stat-lbl">Active students</div></div>
            <div class="st-stat"><div class="st-stat-num" style="color:#10B981;">KSh {{ fNum(active.stats && active.stats.unsettled_revenue) }}</div><div class="st-stat-lbl">Unsettled revenue</div></div>
            <div class="st-stat"><div class="st-stat-num" style="color:#F59E0B;">{{ active.stats && active.stats.outstanding_parent_invoices || 0 }}</div><div class="st-stat-lbl">Outstanding invoices</div></div>
          </div>

          <!-- ============ SCHOOL PROFILE ============ -->
          <div style="font-size:11px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin-bottom:8px;"><i class="mdi mdi-domain me-1"></i>School profile</div>
          <div class="row g-2 mb-3">
            <div class="col-md-6"><label class="st-label">Name</label><input class="st-input" v-model="profileForm.name" /></div>
            <div class="col-md-3"><label class="st-label">Type</label>
              <select class="st-select" v-model="profileForm.type">
                <option value="primary">Primary</option><option value="secondary">Secondary</option>
                <option value="university">University</option><option value="college">College</option>
                <option value="boarding">Boarding</option><option value="mixed">Mixed</option>
              </select>
            </div>
            <div class="col-md-3"><label class="st-label">City</label><input class="st-input" v-model="profileForm.city" /></div>
          </div>
          <div class="row g-2 mb-3">
            <div class="col-md-6"><label class="st-label">Email</label><input type="email" class="st-input" v-model="profileForm.email" /></div>
            <div class="col-md-6"><label class="st-label">Phone</label><input class="st-input" v-model="profileForm.phone" /></div>
          </div>
          <div class="d-flex gap-2 mb-4">
            <button class="btn st-btn-blue btn-sm" @click="saveProfile" :disabled="profileSaving">
              <span v-if="profileSaving" class="spinner-border spinner-border-sm me-1"></span>{{ profileSaving ? 'Saving...' : 'Save School Profile' }}
            </button>
            <span v-if="profileMsg" style="color:#10B981;font-size:12px;align-self:center;">{{ profileMsg }}</span>
          </div>

          <div style="font-size:11px;color:#8BA4C4;text-transform:uppercase;font-weight:700;letter-spacing:.5px;margin-bottom:8px;"><i class="mdi mdi-cash-multiple me-1"></i>Billing rates</div>

          <!-- Charging mode — overrides everything below. While Free, the platform skips
               POS commission, card-fee auto-deduction, and term invoices for this school. -->
          <div class="row g-3 mb-1">
            <div class="col-md-12">
              <label class="st-label">Charging mode <span style="color:#F59E0B;">★</span></label>
              <select class="st-select" v-model.number="form.isFree" :style="{borderColor: form.isFree === 0 ? '#10B981' : '#F59E0B'}">
                <option :value="1">Free — no platform charges (POS commission, card fee, term invoices all skipped)</option>
                <option :value="0">Paid — apply all platform charges</option>
              </select>
              <small :style="{color: form.isFree === 0 ? '#10B981' : '#F59E0B', fontSize:'11px;', fontWeight:'600'}">
                <span v-if="form.isFree === 1">⚠ This institution is on FREE TRIAL — wallet top-ups are NOT charged the card fee, and POS sales do NOT collect the 5% commission. Switch to "Paid" to enable charging.</span>
                <span v-else>✓ Live billing — every wallet top-up auto-deducts the card fee, every POS sale collects platform commission.</span>
              </small>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <label class="st-label">Attendance billing mode</label>
              <select class="st-select" v-model="form.attendanceBillingMode">
                <option value="direct_parent">Direct to parent — each parent pays via portal</option>
                <option value="bundled_in_fees">Bundled in school fees — school is invoiced</option>
                <option value="free_trial">Free trial — nobody pays</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="st-label">Rate per student / term</label>
              <input type="number" min="0" step="50" class="st-input" v-model.number="form.attendanceRate" />
            </div>
            <div class="col-md-3">
              <label class="st-label">Frequency</label>
              <select class="st-select" v-model="form.billingFrequency">
                <option value="per_term">Per term</option>
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div class="row g-3 mt-1">
            <div class="col-md-4">
              <label class="st-label">POS commission %</label>
              <input type="number" min="0" max="100" step="0.5" class="st-input" v-model.number="form.posCommissionPct" />
              <small style="color:#8BA4C4;font-size:11px;">Charged on every wallet POS sale</small>
            </div>
            <div class="col-md-4">
              <label class="st-label">Card issuance fee (KSh)</label>
              <input type="number" min="0" step="50" class="st-input" v-model.number="form.cardIssuanceFee" />
            </div>
            <div class="col-md-4">
              <label class="st-label">Card validity (months)</label>
              <input type="number" min="1" max="60" class="st-input" v-model.number="form.cardValidityMonths" />
            </div>
          </div>

          <div class="row g-3 mt-1">
            <div class="col-md-4">
              <label class="st-label">Free until (optional)</label>
              <input type="date" class="st-input" v-model="form.freeUntil" />
              <small style="color:#8BA4C4;font-size:11px;">Skip billing while in trial</small>
            </div>
            <div class="col-md-4">
              <label class="st-label">Billing status</label>
              <select class="st-select" v-model="form.billingStatus">
                <option value="free">Free</option>
                <option value="paid">Paid</option>
                <option value="grace_period">Grace period</option>
                <option value="overdue">Overdue</option>
                <option value="locked">Locked</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="st-label">Grace period (days)</label>
              <input type="number" min="0" max="60" class="st-input" v-model.number="form.gracePeriodDays" />
            </div>
          </div>

          <div class="d-flex gap-2 mt-4 flex-wrap">
            <button class="btn st-btn-primary" @click="saveBilling" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              {{ saving ? 'Saving...' : 'Save Rates' }}
            </button>
            <button class="btn st-btn-green" @click="generateInvoices" :disabled="saving" title="Generate per-student-per-term invoices for the current term">
              <i class="mdi mdi-receipt me-1"></i>Generate Term Invoices
            </button>
            <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;margin-left:auto;" @click="closeBilling">Close</button>
          </div>

          <!-- ============ PAYBILLS SECTION ============ -->
          <div style="margin-top:24px;border-top:1px solid #1A3A6A;padding-top:20px;">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div>
                <div style="color:#E8EDF4;font-weight:700;font-size:15px;"><i class="mdi mdi-cellphone-key me-1" style="color:#10B981;"></i>M-Pesa Paybills</div>
                <div style="color:#8BA4C4;font-size:11px;">Configure Daraja credentials. The primary paybill is used for STK push.</div>
              </div>
              <button class="st-btn st-btn-sm st-btn-green" @click="openAddPaybill"><i class="mdi mdi-plus me-1"></i>Add Paybill</button>
            </div>
            <div v-if="paybillsLoading" class="st-spinner"><div class="spinner-border"></div></div>
            <div v-else-if="paybills.length" style="overflow-x:auto;">
              <table class="st-table">
                <thead><tr><th>Shortcode</th><th>Type</th><th>Prefix</th><th>Purpose</th><th>Env</th><th>Cred.</th><th>Primary</th><th>Status</th><th title="Mark one paybill as the platform's collection account (PINV-#### auto-routes here)">Platform</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="pb in paybills" :key="pb.id">
                    <td style="font-weight:700;color:#E8EDF4;">{{ pb.shortcode }}</td>
                    <td>{{ pb.type }}</td>
                    <td>{{ pb.account_prefix || '—' }}</td>
                    <td>{{ pb.purpose }}</td>
                    <td><span class="st-badge" :class="pb.environment === 'production' ? 'st-badge-success' : 'st-badge-warning'">{{ pb.environment }}</span></td>
                    <td>
                      <i v-if="pb.has_credentials" class="mdi mdi-check-circle" style="color:#10B981;"></i>
                      <i v-else class="mdi mdi-alert-circle" style="color:#F59E0B;"></i>
                    </td>
                    <td>
                      <i v-if="pb.is_primary == 1" class="mdi mdi-star" style="color:#F59E0B;"></i>
                      <span v-else style="color:#8BA4C4;">—</span>
                    </td>
                    <td><span class="st-badge" :class="pb.is_active==1?'st-badge-success':'st-badge-danger'">{{ pb.is_active==1?'Active':'Inactive' }}</span></td>
                    <td>
                      <button class="st-btn st-btn-sm" :class="pb.is_platform_paybill==1 ? '' : ''" :style="pb.is_platform_paybill==1 ? 'background:linear-gradient(135deg,#F59E0B,#D97706);color:#fff;border:none;font-weight:700;' : 'background:rgba(245,158,11,.1);color:#8BA4C4;border:1px dashed rgba(245,158,11,.3);'" @click="togglePlatformPaybill(pb)" :title="pb.is_platform_paybill==1 ? 'This is the platform paybill — invoices route here' : 'Mark as platform paybill (only one at a time)'">
                        <i class="mdi" :class="pb.is_platform_paybill==1 ? 'mdi-bank-check' : 'mdi-bank-outline'"></i>
                      </button>
                    </td>
                    <td style="white-space:nowrap;">
                      <button class="st-btn st-btn-sm st-btn-blue me-1" @click="testConnection(pb)" :disabled="pb._busy" title="Test Daraja connection"><i class="mdi mdi-lan-connect"></i></button>
                      <button class="st-btn st-btn-sm st-btn-green me-1" @click="openTestStk(pb)" :disabled="pb._busy" title="Send a real STK push"><i class="mdi mdi-cellphone-arrow-down"></i></button>
                      <button class="st-btn st-btn-sm me-1" style="background:rgba(168,85,247,.15);color:#A855F7;border:1px solid rgba(168,85,247,.3);" @click="openTestB2B(pb)" :disabled="pb._busy" title="Test B2B payout"><i class="mdi mdi-bank-transfer"></i></button>
                      <button class="st-btn st-btn-sm me-1" :class="pb.callbacks_registered == 1 ? 'st-btn-blue' : ''" :style="pb.callbacks_registered == 1 ? '' : 'background:rgba(245,158,11,.15);color:#F59E0B;border:1px solid rgba(245,158,11,.3);'" @click="activatePaybill(pb)" :disabled="pb._busy" :title="pb.callbacks_registered == 1 ? 'Re-register C2B URLs' : 'Activate (register C2B URLs)'"><i class="mdi" :class="pb.callbacks_registered == 1 ? 'mdi-shield-check' : 'mdi-shield-alert'"></i></button>
                      <button class="st-btn st-btn-sm st-btn-blue me-1" @click="openEditPaybill(pb)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                      <button class="st-btn st-btn-sm" style="background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);" @click="deletePaybill(pb)" :disabled="pb._busy" title="Delete"><i class="mdi mdi-trash-can"></i></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else style="padding:14px;text-align:center;color:#8BA4C4;font-size:12px;border:1px dashed #1A3A6A;border-radius:8px;">
              No paybills configured. Click <strong>+ Add Paybill</strong> to add one.
            </div>
          </div>

          <!-- ============ PLATFORM INVOICES SECTION ============ -->
          <div style="margin-top:24px;border-top:1px solid #1A3A6A;padding-top:20px;">
            <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div>
                <div style="color:#E8EDF4;font-weight:700;font-size:15px;"><i class="mdi mdi-receipt-text me-1" style="color:#4FC3F7;"></i>Platform Invoices</div>
                <div style="color:#8BA4C4;font-size:11px;">Bill the school for accrued platform revenue. Each invoice claims a date range; days you skip stay re-billable.</div>
              </div>
              <button class="st-btn st-btn-sm st-btn-primary" @click="openGenerateInvoice"><i class="mdi mdi-receipt-text-plus me-1"></i>Generate Invoice</button>
            </div>

            <!-- Coverage timeline — shows existing invoices (filled blocks) + unbilled spans (amber gaps).
                 Span "amount" is the NET billable (already-prepaid portion subtracted). When net < gross, a small
                 secondary line surfaces the prepaid context so the user understands why "owed" < "accrued". -->
            <div v-if="inv.unbilled_spans && inv.unbilled_spans.length" style="background:rgba(245,158,11,.05);border:1px solid rgba(245,158,11,.3);border-radius:8px;padding:10px 12px;margin-bottom:12px;">
              <div style="color:#F59E0B;font-size:11px;font-weight:700;letter-spacing:.4px;margin-bottom:6px;">
                ⚠ UNBILLED SPANS — {{ inv.unbilled_spans.length }} gap{{ inv.unbilled_spans.length === 1 ? '' : 's' }} · net to bill <strong>KSh {{ fNum(inv.unbilled_spans.reduce((a,s)=>a+Number(s.amount),0)) }}</strong>
              </div>
              <div class="d-flex gap-2 flex-wrap">
                <button v-for="s in inv.unbilled_spans" :key="s.start" class="st-btn st-btn-sm" style="background:rgba(245,158,11,.15);color:#F59E0B;border:1px solid rgba(245,158,11,.4);font-size:11px;text-align:left;line-height:1.3;" @click="prefillInvoiceRange(s)" :title="`${s.count} line(s) — click to pre-fill the range`">
                  {{ s.start.substr(0, 10) }} → {{ s.end.substr(0, 10) }} · KSh {{ fNum(s.amount) }}
                </button>
              </div>
            </div>

            <div v-if="inv.loading" class="st-spinner"><div class="spinner-border"></div></div>
            <div v-else-if="inv.list && inv.list.length" style="overflow-x:auto;">
              <table class="st-table">
                <thead><tr><th>Invoice #</th><th>Period</th><th>Lines</th><th>Amount</th><th>Paid</th><th>Status</th><th>Issued</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="i in inv.list" :key="i.id" :style="i.status === 'void' ? 'opacity:.5;' : ''">
                    <td style="font-weight:700;font-family:monospace;color:#4FC3F7;">{{ i.invoice_no }}</td>
                    <td style="font-size:12px;">{{ i.period_start.substr(0, 10) }} → {{ i.period_end.substr(0, 10) }}</td>
                    <td>{{ i.line_count }}</td>
                    <td style="font-weight:700;">KSh {{ fNum(i.amount) }}</td>
                    <td :style="{color: Number(i.paid_amount) >= Number(i.amount) ? '#10B981' : (Number(i.paid_amount) > 0 ? '#F59E0B' : '#8BA4C4')}">KSh {{ fNum(i.paid_amount) }}</td>
                    <td>
                      <span class="st-badge" :class="invStatusBadge(i.status)">{{ i.status }}</span>
                      <div v-if="i.status === 'partial'" style="font-size:10px;color:#F59E0B;margin-top:2px;">−KSh {{ fNum(i.outstanding) }}</div>
                    </td>
                    <td style="font-size:11px;color:#8BA4C4;">{{ i.issued_at ? i.issued_at.substr(0, 10) : '—' }}</td>
                    <td style="white-space:nowrap;">
                      <button class="st-btn st-btn-sm st-btn-blue me-1" @click="printInvoice(i)" title="Print or download as PDF">
                        <i class="mdi mdi-printer"></i>
                      </button>
                      <button v-if="i.status !== 'void' && Number(i.outstanding) > 0" class="st-btn st-btn-sm me-1" style="background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.4);" @click="payAgainstInvoice(i)" title="Record settlement against this invoice">
                        <i class="mdi mdi-cash-plus"></i>
                      </button>
                      <button v-if="i.status === 'pending'" class="st-btn st-btn-sm" style="background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);" @click="voidInvoice(i)" title="Void — un-claim its lines so they can be re-invoiced">
                        <i class="mdi mdi-close-circle"></i>
                      </button>
                      <span v-else-if="i.status === 'void'" style="color:#EF4444;font-size:11px;">Voided</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else style="padding:14px;text-align:center;color:#8BA4C4;font-size:12px;border:1px dashed #1A3A6A;border-radius:8px;">
              No invoices yet. Click <strong>Generate Invoice</strong> to bill accrued platform revenue.
            </div>
          </div>

          <!-- ============ SETTLEMENTS SECTION ============ -->
          <div style="margin-top:24px;border-top:1px solid #1A3A6A;padding-top:20px;">
            <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div>
                <div style="color:#E8EDF4;font-weight:700;font-size:15px;"><i class="mdi mdi-cash-multiple me-1" style="color:#10B981;"></i>Platform Settlements</div>
                <div style="color:#8BA4C4;font-size:11px;">Payments this school has sent to the platform's paybill. FIFO-allocated against accrued revenue.</div>
              </div>
              <button class="st-btn st-btn-sm" style="background:linear-gradient(135deg,#10B981,#059669);color:#fff;border:none;font-weight:600;" @click="openSettlement(active.institution)">
                <i class="mdi mdi-cash-plus me-1"></i>Record Payment
              </button>
            </div>

            <!-- Outstanding mini-stats -->
            <div class="d-flex gap-2 mb-3 flex-wrap">
              <div style="background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:10px 14px;flex:1;min-width:140px;">
                <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">ACCRUED</div>
                <div style="color:#E8EDF4;font-size:18px;font-weight:800;">KSh {{ fNum(settle.accrued || 0) }}</div>
              </div>
              <div style="background:#0A1628;border:1px solid rgba(16,185,129,.4);border-radius:8px;padding:10px 14px;flex:1;min-width:140px;">
                <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">PAID</div>
                <div style="color:#10B981;font-size:18px;font-weight:800;">KSh {{ fNum(settle.settled || 0) }}</div>
              </div>
              <div style="background:#0A1628;border:1px solid rgba(245,158,11,.4);border-radius:8px;padding:10px 14px;flex:1;min-width:140px;">
                <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">OUTSTANDING</div>
                <div :style="{color: Number(settle.outstanding || 0) > 0 ? '#F59E0B' : '#8BA4C4', fontSize: '18px', fontWeight: 800}">KSh {{ fNum(settle.outstanding || 0) }}</div>
              </div>
            </div>

            <div v-if="settle.loading" class="st-spinner"><div class="spinner-border"></div></div>
            <div v-else-if="settle.list && settle.list.length" style="overflow-x:auto;">
              <table class="st-table">
                <thead><tr><th>Paid at</th><th>Amount</th><th>Allocated</th><th>M-Pesa</th><th>Source</th><th>Recorded by</th><th>Notes</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="s in settle.list" :key="s.id" :style="s.reversed_at ? 'opacity:.5;' : ''">
                    <td style="font-size:12px;">{{ s.paid_at }}</td>
                    <td style="font-weight:700;color:#10B981;">KSh {{ fNum(s.amount) }}</td>
                    <td style="font-size:12px;">
                      <span :style="{color: Number(s.allocated_amount) >= Number(s.amount) ? '#10B981' : '#F59E0B'}">KSh {{ fNum(s.allocated_amount) }}</span>
                      <span v-if="Number(s.allocated_amount) < Number(s.amount) && !s.reversed_at" style="color:#F59E0B;font-size:10px;display:block;">Unallocated: KSh {{ fNum(s.amount - s.allocated_amount) }}</span>
                    </td>
                    <td style="font-family:monospace;font-size:12px;">{{ s.mpesa_code || '—' }}</td>
                    <td><span class="st-badge" :class="s.source === 'manual' ? 'st-badge-info' : 'st-badge-success'" style="font-size:10px;">{{ s.source }}</span></td>
                    <td style="font-size:12px;color:#8BA4C4;">{{ s.recorded_by_name || '—' }}</td>
                    <td style="font-size:12px;color:#8BA4C4;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" :title="s.notes">{{ s.notes || '—' }}</td>
                    <td>
                      <span v-if="s.reversed_at" style="color:#EF4444;font-size:11px;">↺ Reversed</span>
                      <button v-else class="st-btn st-btn-sm" style="background:rgba(239,68,68,.15);color:#EF4444;border:1px solid rgba(239,68,68,.3);" @click="reverseSettlement(s)" title="Reverse — un-allocate the platform_revenue rows it covered">
                        <i class="mdi mdi-undo"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else style="padding:14px;text-align:center;color:#8BA4C4;font-size:12px;border:1px dashed #1A3A6A;border-radius:8px;">
              No settlements yet. Click <strong>Record Payment</strong> when this school sends money to the platform's paybill.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============ RECORD SETTLEMENT MODAL ============ -->
    <div class="st-modal-overlay" v-if="showRecordSettlement" @click.self="closeSettlement">
      <div class="st-modal" style="max-width:520px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-cash-plus me-2"></i>Record Platform Payment</span>
          <button class="st-modal-close" @click="closeSettlement">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="settleForm.error">{{ settleForm.error }}</div>
        <div class="st-alert st-alert-success" v-if="settleForm.success">{{ settleForm.success }}</div>

        <div style="background:rgba(46,134,222,.05);border:1px solid #1A3A6A;border-radius:8px;padding:12px;margin-bottom:14px;">
          <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">SCHOOL</div>
          <div style="color:#E8EDF4;font-size:15px;font-weight:700;">{{ settleForm.institutionName }}</div>
          <div style="color:#F59E0B;font-size:12px;margin-top:4px;">Currently owed: <strong>KSh {{ fNum(settleForm.outstanding) }}</strong></div>
        </div>

        <div class="row g-3">
          <div class="col-12">
            <label class="st-label">Amount received (KSh) *</label>
            <input type="number" min="1" step="0.01" class="st-input" v-model.number="settleForm.amount" placeholder="e.g. 11250" />
          </div>
          <div class="col-12">
            <label class="st-label">M-Pesa code <small style="color:#8BA4C4;">(receipt — must be unique)</small></label>
            <input class="st-input" v-model="settleForm.mpesaCode" placeholder="e.g. UE6NA3C4TV" style="font-family:monospace;text-transform:uppercase;" />
          </div>
          <div class="col-6">
            <label class="st-label">Paid at</label>
            <input type="datetime-local" class="st-input" v-model="settleForm.paidAt" />
          </div>
          <div class="col-6">
            <label class="st-label">Source</label>
            <select class="st-select" v-model="settleForm.source">
              <option value="manual">Manual entry</option>
              <option value="b2b">B2B transfer</option>
              <option value="adjustment">Adjustment</option>
            </select>
          </div>
          <div class="col-12">
            <label class="st-label">Notes</label>
            <textarea class="st-input" rows="2" v-model="settleForm.notes" placeholder="e.g. Confirmation SMS quoted SIMI SYSTEMS shortcode 577442"></textarea>
          </div>
        </div>

        <div class="d-flex gap-2 mt-4">
          <button class="btn st-btn-primary flex-fill" @click="saveSettlement" :disabled="settleForm.saving || !settleForm.amount">
            <span v-if="settleForm.saving" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-check me-1"></i>{{ settleForm.saving ? 'Recording…' : 'Record Settlement' }}
          </button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:12px 20px;" @click="closeSettlement" :disabled="settleForm.saving">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ============ GENERATE INVOICE MODAL ============ -->
    <div class="st-modal-overlay" v-if="showGenInvoice" @click.self="closeGenInvoice">
      <div class="st-modal" style="max-width:560px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-receipt-text-plus me-2"></i>Generate Platform Invoice</span>
          <button class="st-modal-close" @click="closeGenInvoice">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="genForm.error">{{ genForm.error }}</div>
        <div class="st-alert st-alert-success" v-if="genForm.success">{{ genForm.success }}</div>

        <div style="background:rgba(46,134,222,.05);border:1px solid #1A3A6A;border-radius:8px;padding:12px;margin-bottom:14px;">
          <div style="color:#8BA4C4;font-size:11px;font-weight:600;letter-spacing:.3px;">SCHOOL</div>
          <div style="color:#E8EDF4;font-size:15px;font-weight:700;">{{ genForm.institutionName }}</div>
        </div>

        <div class="row g-3">
          <div class="col-6">
            <label class="st-label">Period start</label>
            <input type="date" class="st-input" v-model="genForm.start" @change="runPreview" />
          </div>
          <div class="col-6">
            <label class="st-label">Period end</label>
            <input type="date" class="st-input" v-model="genForm.end" @change="runPreview" />
          </div>
          <div class="col-12">
            <label class="st-label">Notes (optional)</label>
            <textarea class="st-input" rows="2" v-model="genForm.notes" placeholder="e.g. Monthly invoice — May 2026"></textarea>
          </div>
        </div>

        <!-- Preview panel — appears after dates change -->
        <div v-if="genForm.preview" style="margin-top:12px;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:12px;">
          <div style="color:#8BA4C4;font-size:11px;font-weight:700;letter-spacing:.3px;margin-bottom:6px;">PREVIEW</div>
          <div style="display:flex;gap:14px;flex-wrap:wrap;">
            <div>
              <div style="color:#8BA4C4;font-size:10px;">NET TO BILL</div>
              <div style="color:#10B981;font-size:18px;font-weight:800;">KSh {{ fNum(genForm.preview.will_include_amount) }}</div>
              <div style="color:#8BA4C4;font-size:10px;">{{ genForm.preview.will_include_count }} line{{ genForm.preview.will_include_count === 1 ? '' : 's' }}</div>
            </div>
            <div v-if="genForm.preview.will_skip_count > 0">
              <div style="color:#8BA4C4;font-size:10px;">SKIP (already on another invoice)</div>
              <div style="color:#F59E0B;font-size:14px;font-weight:700;">{{ genForm.preview.will_skip_count }} lines · KSh {{ fNum(genForm.preview.will_skip_amount) }}</div>
            </div>
          </div>
          <div v-if="genForm.preview.overlap_invoices && genForm.preview.overlap_invoices.length" style="margin-top:8px;font-size:11px;color:#F59E0B;">
            ⚠ Overlap with: <strong>{{ genForm.preview.overlap_invoices.map(o => o.invoice_no).join(', ') }}</strong> — those lines will be skipped.
          </div>
          <div v-if="genForm.preview.will_include_count === 0" style="margin-top:8px;font-size:12px;color:#EF4444;">
            No unbilled platform-revenue rows in this range. Nothing to invoice.
          </div>
          <div v-else-if="Number(genForm.preview.will_include_amount) <= 0" style="margin-top:8px;font-size:12px;color:#EF4444;">
            Every row in this range has already been fully paid. Nothing left to bill.
          </div>
        </div>

        <div class="d-flex gap-2 mt-4">
          <button class="btn st-btn-primary flex-fill" @click="confirmGenerate" :disabled="genForm.saving || !genForm.preview || genForm.preview.will_include_count === 0">
            <span v-if="genForm.saving" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="mdi mdi-check me-1"></i>{{ genForm.saving ? 'Issuing…' : 'Issue Invoice' }}
          </button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:12px 20px;" @click="closeGenInvoice" :disabled="genForm.saving">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ============ TEST STK MODAL ============ -->
    <div class="st-modal-overlay" v-if="showTestStk" @click.self="closeTestStk">
      <div class="st-modal" style="max-width:480px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-cellphone-arrow-down me-2"></i>Test STK Push</span>
          <button class="st-modal-close" @click="closeTestStk">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="testStkError">{{ testStkError }}</div>

        <div v-if="testStkPaybill" style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;margin-bottom:14px;">
          <div style="color:#E8EDF4;font-weight:700;">{{ testStkPaybill.shortcode }} <span style="font-weight:400;color:#8BA4C4;">· {{ testStkPaybill.environment }}</span></div>
          <div style="color:#8BA4C4;font-size:12px;margin-top:2px;">A real M-Pesa prompt will be sent — use a small amount (KSh 1).</div>
        </div>

        <div class="row g-2 mb-3">
          <div class="col-7"><label class="st-label">Phone (your test number)</label><input class="st-input" v-model="testStkForm.phone" placeholder="0712345678" :disabled="testStkSending || (testStkStatus.stage && testStkStatus.stage !== 'idle')" /></div>
          <div class="col-5"><label class="st-label">Amount (KSh)</label><input type="number" min="1" class="st-input" v-model.number="testStkForm.amount" :disabled="testStkSending || (testStkStatus.stage && testStkStatus.stage !== 'idle')" /></div>
        </div>

        <!-- LIVE STATUS PANEL -->
        <div v-if="testStkStatus.stage && testStkStatus.stage !== 'idle'" :style="testStkStatus.style" style="border-radius:10px;padding:14px;margin-bottom:14px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <i v-if="testStkStatus.stage === 'sending' || testStkStatus.stage === 'pending'" class="spinner-border spinner-border-sm" :style="{color: testStkStatus.iconColor}"></i>
            <i v-else class="mdi" :class="testStkStatus.icon" :style="{color: testStkStatus.iconColor, fontSize:'22px'}"></i>
            <div style="flex:1;">
              <div :style="{color: testStkStatus.titleColor, fontWeight:700, fontSize:'14px'}">{{ testStkStatus.title }}</div>
              <div style="color:#8BA4C4;font-size:11px;margin-top:2px;">{{ testStkStatus.detail }}</div>
            </div>
          </div>
          <div v-if="testStkStatus.stage === 'pending'" style="margin-top:8px;color:#8BA4C4;font-size:11px;">Polling… elapsed: {{ testStkElapsed }}s · timeout: 90s</div>
          <div v-if="testStkStatus.mpesaReceipt" style="margin-top:8px;color:#10B981;font-size:12px;font-weight:700;">M-Pesa: {{ testStkStatus.mpesaReceipt }}</div>
        </div>

        <div class="d-flex gap-2">
          <button v-if="!testStkStatus.stage || testStkStatus.stage === 'idle'" class="btn st-btn-primary flex-fill" @click="confirmTestStk" :disabled="testStkSending">
            <span v-if="testStkSending" class="spinner-border spinner-border-sm me-1"></span>{{ testStkSending ? 'Sending...' : 'Send Test STK' }}
          </button>
          <button v-else-if="testStkStatus.stage === 'paid' || testStkStatus.stage === 'cancelled' || testStkStatus.stage === 'failed' || testStkStatus.stage === 'timeout'" class="btn st-btn-blue flex-fill" @click="resetTestStk">Test Again</button>
          <button v-else class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;flex:1;" @click="closeTestStk">Stop & Close</button>
          <button v-if="!testStkStatus.stage || ['idle','paid','cancelled','failed','timeout'].includes(testStkStatus.stage)" class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeTestStk">Close</button>
        </div>
      </div>
    </div>

    <!-- ============ TEST B2B MODAL ============ -->
    <div class="st-modal-overlay" v-if="showTestB2B" @click.self="closeTestB2B">
      <div class="st-modal" style="max-width:520px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-bank-transfer me-2"></i>Test B2B Payout</span>
          <button class="st-modal-close" @click="closeTestB2B">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="testB2BError">{{ testB2BError }}</div>

        <div v-if="testB2BPaybill" style="background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:12px;margin-bottom:14px;">
          <div style="color:#E8EDF4;font-weight:700;">From: {{ testB2BPaybill.shortcode }} <span style="font-weight:400;" :style="{color: testB2BPaybill.environment === 'production' ? '#10B981' : '#F59E0B'}">· {{ testB2BPaybill.environment }}</span></div>
          <div style="color:#8BA4C4;font-size:12px;margin-top:2px;">B2B uses Initiator + Security Credential. <strong v-if="testB2BPaybill.environment === 'production'" style="color:#F59E0B;">Production — real money will move.</strong></div>
        </div>

        <div class="row g-2 mb-3">
          <div class="col-7"><label class="st-label">Destination shortcode *</label><input class="st-input" v-model="testB2BForm.destinationShortcode" placeholder="paybill or till" :disabled="testB2BSending || (testB2BStatus.stage && testB2BStatus.stage !== 'idle')" /></div>
          <div class="col-5"><label class="st-label">Amount (KSh)</label><input type="number" min="1" class="st-input" v-model.number="testB2BForm.amount" :disabled="testB2BSending || (testB2BStatus.stage && testB2BStatus.stage !== 'idle')" /></div>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-7"><label class="st-label">Account reference</label><input class="st-input" v-model="testB2BForm.accountReference" placeholder="optional" :disabled="testB2BSending || (testB2BStatus.stage && testB2BStatus.stage !== 'idle')" /></div>
          <div class="col-5"><label class="st-label">Command</label>
            <select class="st-select" v-model="testB2BForm.commandId" :disabled="testB2BSending || (testB2BStatus.stage && testB2BStatus.stage !== 'idle')">
              <option value="BusinessPayBill">Pay Bill</option>
              <option value="BusinessBuyGoods">Buy Goods</option>
              <option value="MerchantToMerchantTransfer">Merchant → Merchant</option>
            </select>
          </div>
        </div>
        <div class="mb-3" v-if="!testB2BStatus.stage || testB2BStatus.stage === 'idle'">
          <label class="st-label">Remarks</label>
          <input class="st-input" v-model="testB2BForm.remarks" placeholder="Simi Trac B2B Test" />
        </div>

        <!-- LIVE STATUS PANEL -->
        <div v-if="testB2BStatus.stage && testB2BStatus.stage !== 'idle'" :style="testB2BStatus.style" style="border-radius:10px;padding:14px;margin-bottom:14px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <i v-if="testB2BStatus.stage === 'sending' || testB2BStatus.stage === 'pending'" class="spinner-border spinner-border-sm" :style="{color: testB2BStatus.iconColor}"></i>
            <i v-else class="mdi" :class="testB2BStatus.icon" :style="{color: testB2BStatus.iconColor, fontSize:'22px'}"></i>
            <div style="flex:1;">
              <div :style="{color: testB2BStatus.titleColor, fontWeight:700, fontSize:'14px'}">{{ testB2BStatus.title }}</div>
              <div style="color:#8BA4C4;font-size:11px;margin-top:2px;">{{ testB2BStatus.detail }}</div>
            </div>
          </div>
          <div v-if="testB2BStatus.stage === 'pending'" style="margin-top:8px;color:#8BA4C4;font-size:11px;">Waiting for Daraja callback… elapsed: {{ testB2BElapsed }}s · timeout: 120s</div>
          <div v-if="testB2BStatus.transactionId" style="margin-top:8px;color:#10B981;font-size:12px;font-weight:700;">M-Pesa: {{ testB2BStatus.transactionId }}</div>
          <div v-if="testB2BStatus.conversationId" style="margin-top:4px;color:#8BA4C4;font-size:11px;font-family:monospace;">Conversation: {{ testB2BStatus.conversationId }}</div>
        </div>

        <div class="d-flex gap-2">
          <button v-if="!testB2BStatus.stage || testB2BStatus.stage === 'idle'" class="btn st-btn-primary flex-fill" @click="confirmTestB2B" :disabled="testB2BSending">
            <span v-if="testB2BSending" class="spinner-border spinner-border-sm me-1"></span>{{ testB2BSending ? 'Sending...' : 'Send B2B Request' }}
          </button>
          <button v-else-if="['paid','failed','timeout'].includes(testB2BStatus.stage)" class="btn st-btn-blue flex-fill" @click="resetTestB2B">Test Again</button>
          <button v-else class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;flex:1;" @click="closeTestB2B">Stop & Close</button>
          <button v-if="!testB2BStatus.stage || ['idle','paid','failed','timeout'].includes(testB2BStatus.stage)" class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closeTestB2B">Close</button>
        </div>
      </div>
    </div>

    <!-- ============ PAYBILL ADD/EDIT MODAL ============ -->
    <div class="st-modal-overlay" v-if="showPaybillForm" @click.self="closePaybillForm">
      <div class="st-modal" style="max-width:640px;">
        <div class="st-modal-title">
          <span><i class="mdi mdi-cellphone-key me-2"></i>{{ pbForm.id ? 'Edit Paybill' : 'Add Paybill' }} — {{ active.institution && active.institution.name }}</span>
          <button class="st-modal-close" @click="closePaybillForm">&times;</button>
        </div>
        <div class="st-alert st-alert-danger" v-if="pbError">{{ pbError }}</div>

        <div class="row g-2 mb-3">
          <div class="col-md-5"><label class="st-label">Shortcode *</label><input class="st-input" v-model="pbForm.shortcode" placeholder="174379" /></div>
          <div class="col-md-4"><label class="st-label">Type</label>
            <select class="st-select" v-model="pbForm.type"><option value="paybill">Paybill</option><option value="till">Till</option></select>
          </div>
          <div class="col-md-3"><label class="st-label">Environment</label>
            <select class="st-select" v-model="pbForm.environment"><option value="sandbox">Sandbox</option><option value="production">Production</option></select>
          </div>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-md-6"><label class="st-label">Label</label><input class="st-input" v-model="pbForm.label" placeholder="M-Pesa" /></div>
          <div class="col-md-3"><label class="st-label">Account Prefix</label><input class="st-input" v-model="pbForm.accountPrefix" placeholder="SPI" /></div>
          <div class="col-md-3"><label class="st-label">Purpose</label>
            <select class="st-select" v-model="pbForm.purpose">
              <option value="all">All</option>
              <option value="wallet_topup">Wallet top-up</option>
              <option value="fee_payment">Fee payment</option>
              <option value="pos_payment">POS payment</option>
              <option value="sms_purchase">SMS purchase</option>
              <option value="donation">Donation</option>
            </select>
          </div>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-md-6"><label class="st-label">Consumer Key {{ pbForm.id ? '(leave blank to keep)' : '*' }}</label><input class="st-input" v-model="pbForm.consumerKey" :placeholder="pbForm.id ? '••••••••' : 'paste from Daraja'" /></div>
          <div class="col-md-6"><label class="st-label">Consumer Secret {{ pbForm.id ? '(leave blank to keep)' : '*' }}</label><input class="st-input" v-model="pbForm.consumerSecret" :placeholder="pbForm.id ? '••••••••' : 'paste from Daraja'" /></div>
        </div>
        <div class="row g-2 mb-3">
          <div class="col-md-6"><label class="st-label">Passkey {{ pbForm.id ? '(leave blank to keep)' : '' }}</label><input class="st-input" v-model="pbForm.passkey" :placeholder="pbForm.id ? '••••••••' : 'STK push passkey'" /></div>
          <div class="col-md-6"><label class="st-label">Initiator Name</label><input class="st-input" v-model="pbForm.initiatorName" placeholder="for B2C / refunds" /></div>
        </div>
        <div class="d-flex align-items-center gap-3 mb-3">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;color:#E8EDF4;font-size:13px;"><input type="checkbox" v-model="pbForm.isPrimary" /> Primary (used for STK push)</label>
          <label v-if="pbForm.id" style="display:flex;align-items:center;gap:8px;cursor:pointer;color:#E8EDF4;font-size:13px;"><input type="checkbox" v-model="pbForm.isActive" /> Active</label>
        </div>
        <div class="d-flex gap-2">
          <button class="btn st-btn-primary flex-fill" @click="savePaybill" :disabled="pbSaving">
            <span v-if="pbSaving" class="spinner-border spinner-border-sm me-1"></span>{{ pbSaving ? 'Saving...' : (pbForm.id ? 'Update Paybill' : 'Add Paybill') }}
          </button>
          <button class="btn" style="border:1px solid #1A3A6A;color:#8BA4C4;border-radius:10px;padding:10px 18px;" @click="closePaybillForm">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
import { printPlatformInvoice } from "@/utils/printPlatformInvoice";
export default {
  name: "PlatformAdmin",
  components: { Navigation },
  data() {
    return {
      loading: true,
      kpis: {},
      institutions: [],
      recentRevenue: [],
      tab: 'institutions',
      search: '',
      // Platform Team
      platformOwners: [],
      showAddOwner: false, addOwnerSaving: false, addOwnerError: '',
      addOwnerSuccess: false, addOwnerTempPassword: '', addOwnerSentVia: '',
      addOwnerForm: { name: '', email: '', phone: '', password: '' },
      // Onboard School
      showOnboard: false, onboardSaving: false, onboardError: '',
      onboardSuccess: false, onboardTempPassword: '',
      onboardForm: { name: '', type: 'mixed', educationType: 'mixed_school', email: '', city: '', adminName: '', phone: '', password: '', billingMode: 'direct_parent', isFree: 1 },
      onboardAppliedPresets: [],
      // All Users
      allUsers: [], usersLoading: false,
      userFilters: { institutionId: '', userType: '', status: 'active', search: '' },
      // Paybills (per-school)
      paybills: [], paybillsLoading: false,
      showPaybillForm: false, pbSaving: false, pbError: '',
      pbForm: { id: null, shortcode: '', type: 'paybill', environment: 'sandbox', label: 'M-Pesa', accountPrefix: '', purpose: 'all', consumerKey: '', consumerSecret: '', passkey: '', initiatorName: '', isPrimary: false, isActive: true },
      // Paybill STK test
      showTestStk: false, testStkPaybill: null, testStkSending: false, testStkError: '',
      testStkForm: { phone: '', amount: 1 },
      // Paybill B2B test
      showTestB2B: false, testB2BPaybill: null, testB2BSending: false, testB2BError: '',
      testB2BForm: { destinationShortcode: '', amount: 10, accountReference: '', commandId: 'BusinessPayBill', remarks: 'Simi Trac B2B Test' },
      testB2BStatus: { stage: 'idle' },
      testB2BConversationId: null,
      testB2BPollHandle: null,
      testB2BStartedAt: 0,
      testB2BElapsed: 0,
      testStkCheckoutId: null,
      testStkStatus: { stage: 'idle' },   // idle | sending | pending | paid | cancelled | failed | timeout
      testStkPollHandle: null,
      testStkStartedAt: 0,
      testStkElapsed: 0,
      // School profile (inside Billing modal)
      profileForm: { name: '', type: 'mixed', email: '', phone: '', city: '' },
      profileSaving: false, profileMsg: '',
      // Platform invoices (inside Billing modal)
      inv: { loading: false, list: [], unbilled_spans: [] },
      // All-invoices tab (cross-institution roll-up with status filter + search)
      allInv: { loading: false, list: [], totals: { count: 0, amount: 0, paid: 0, outstanding: 0 }, status: 'all', search: '', _searchTimer: null },
      showGenInvoice: false,
      genForm: {
        institutionId: null, institutionName: '',
        start: '', end: '', notes: '',
        preview: null, previewing: false, saving: false,
        error: '', success: '',
      },
      // Settlements (inside Billing modal + standalone Record modal)
      settle: { loading: false, list: [], accrued: 0, settled: 0, outstanding: 0 },
      showRecordSettlement: false,
      settleForm: {
        institutionId: null, institutionName: '', outstanding: 0,
        amount: null, mpesaCode: '', paidAt: '', source: 'manual', notes: '',
        saving: false, error: '', success: '',
      },
      // Cash Flow
      cashFlow: { kpis: {}, by_purpose: [], by_method: [], per_school: [], recent: [] },
      // Accounting
      acct: { invoice_totals: {}, per_school: [], collected_this_month: 0, unsettled_revenue: 0, settled_revenue: 0 },
      invoices: [], invFilter: 'pending', acctRunning: false, monthEndResult: '',
      showSettle: false, settleInv: null, settleSaving: false, settleError: '',
      settleForm: { paymentMethod: 'mpesa', paidAmount: 0, mpesaCode: '', notes: '', settleAllPending: false },
      showBilling: false, billingLoading: false, saving: false, billingMsg: '', billingError: '',
      active: { institution: null, profile: null, stats: null },
      form: {
        isFree: 1,
        attendanceBillingMode: 'direct_parent',
        attendanceRate: 1000,
        billingFrequency: 'per_term',
        posCommissionPct: 5.0,
        cardIssuanceFee: 500,
        cardValidityMonths: 12,
        freeUntil: '',
        billingStatus: 'free',
        gracePeriodDays: 7,
      },
    };
  },
  computed: {
    filteredInstitutions() {
      if (!this.search) return this.institutions;
      const s = this.search.toLowerCase();
      return this.institutions.filter(i => (i.name || '').toLowerCase().includes(s) || (i.type || '').toLowerCase().includes(s) || (i.slug || '').toLowerCase().includes(s));
    }
  },
  mounted() { this.loadDashboard(); },
  beforeUnmount() {
    if (this.testStkPollHandle) clearInterval(this.testStkPollHandle);
    if (this.testB2BPollHandle) clearInterval(this.testB2BPollHandle);
  },
  methods: {
    fNum(v) { if (!v && v !== 0) return '0'; return Number(v).toLocaleString('en-KE', { maximumFractionDigits: 2 }); },
    formatDate(s) {
      if (!s) return '-';
      const d = new Date(s.replace(' ', 'T'));
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString('en-KE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    revenueLabel(t) {
      return ({
        pos_commission: 'POS commission',
        card_fee: 'Card fee',
        attendance_parent: 'Parent invoice',
        attendance_school: 'School invoice',
      })[t] || t;
    },
    revenueDescription(t) {
      return ({
        pos_commission: 'Per-transaction commission on wallet POS sales',
        card_fee: 'Card issuance / renewal fees',
        attendance_parent: 'Per-student-per-term fees paid by parents',
        attendance_school: 'Per-student-per-term fees paid by schools (bundled mode)',
      })[t] || '';
    },
    modeLabel(mode, isFree, freeUntil) {
      if (isFree == 1) return 'Free';
      if (freeUntil && new Date(freeUntil) > new Date()) return 'Trial';
      return ({
        direct_parent: 'Direct → parents',
        bundled_in_fees: 'Bundled (school)',
        free_trial: 'Free trial',
      })[mode] || (mode || '-');
    },
    modeBadge(mode, isFree, freeUntil) {
      if (isFree == 1 || mode === 'free_trial' || (freeUntil && new Date(freeUntil) > new Date())) return 'st-badge-warning';
      if (mode === 'direct_parent') return 'st-badge-info';
      if (mode === 'bundled_in_fees') return 'st-badge-success';
      return 'st-badge-info';
    },
    async loadDashboard() {
      this.loading = true;
      try {
        const r = await executeJson({ function: 'getPlatformDashboard' }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.kpis = d.kpis || {};
          this.institutions = d.institutions || [];
          this.recentRevenue = d.recent_revenue || [];
        }
      } catch (e) { console.error(e); }
      this.loading = false;
    },

    // ========== Platform Team ==========
    async onTeamTab() {
      this.tab = 'team';
      await this.loadPlatformOwners();
    },
    async loadPlatformOwners() {
      try {
        const r = await executeJson({ function: 'listPlatformOwners' }, simitrack);
        if (r.data.success) this.platformOwners = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) { /* silent */ }
    },
    resetAddOwner() {
      this.addOwnerSuccess = false;
      this.addOwnerError = '';
      this.addOwnerTempPassword = '';
      this.addOwnerForm = { name: '', email: '', phone: '', password: '' };
    },
    closeAddOwner() {
      this.showAddOwner = false;
      this.resetAddOwner();
    },
    async addOwner() {
      this.addOwnerError = '';
      if (!this.addOwnerForm.name) { this.addOwnerError = 'Name is required'; return; }
      if (!this.addOwnerForm.email && !this.addOwnerForm.phone) { this.addOwnerError = 'Email or phone is required'; return; }
      this.addOwnerSaving = true;
      try {
        const params = { function: 'addPlatformOwner', ...this.addOwnerForm };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.addOwnerTempPassword = (r.data.data && r.data.data.tempPassword) ? r.data.data.tempPassword : (this.addOwnerForm.password || '—');
          this.addOwnerSentVia = (this.addOwnerForm.phone && this.addOwnerForm.email) ? 'SMS and email' : (this.addOwnerForm.phone ? 'SMS' : 'email');
          this.addOwnerSuccess = true;
          await this.loadPlatformOwners();
        } else {
          this.addOwnerError = r.data.message || 'Failed to create';
        }
      } catch (e) { this.addOwnerError = 'Connection error'; }
      this.addOwnerSaving = false;
    },
    // ========== All Users ==========
    userTypeLabel(role) {
      if (['parent'].includes(role)) return 'Parent';
      if (['student','child'].includes(role)) return 'Student';
      return 'Staff';
    },
    userTypeBadge(role) {
      if (['parent'].includes(role)) return 'st-badge-warning';
      if (['student','child'].includes(role)) return 'st-badge-info';
      return 'st-badge-success';
    },
    async onUsersTab() {
      this.tab = 'users';
      if (!this.allUsers.length) await this.loadAllUsers();
    },
    async loadAllUsers() {
      this.usersLoading = true;
      try {
        const r = await executeJson({ function: 'getAllPlatformUsers', ...this.userFilters }, simitrack);
        if (r.data.success) this.allUsers = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) { console.error(e); }
      this.usersLoading = false;
    },
    async resetUserPassword(u) {
      if (!confirm(`Reset password for ${u.name}? A new temp password will be generated and SMS'd${u.email ? '/emailed' : ''} to the user.`)) return;
      try {
        const r = await executeJson({ function: 'adminResetUserPassword', userId: u.id }, simitrack);
        if (r.data.success) {
          const pw = (r.data.data && r.data.data.tempPassword) || '—';
          alert(`Password reset for ${u.name}. New temp password: ${pw}\n\nThe user has been notified by SMS / email.`);
        } else { alert(r.data.message || 'Reset failed'); }
      } catch (e) { alert('Connection error'); }
    },

    // ========== School Profile (inside Billing modal) ==========
    async saveProfile() {
      if (!this.active.institution) return;
      this.profileSaving = true; this.profileMsg = '';
      try {
        const r = await executeJson({ function: 'adminUpdateInstitution', institutionId: this.active.institution.id, ...this.profileForm }, simitrack);
        if (r.data.success) {
          this.profileMsg = 'Profile saved';
          // reflect locally so the dashboard table updates without a full reload
          if (this.active.institution) {
            this.active.institution.name = this.profileForm.name;
            this.active.institution.type = this.profileForm.type;
          }
          this.loadDashboard();
        } else { alert(r.data.message || 'Save failed'); }
      } catch (e) { alert('Connection error'); }
      this.profileSaving = false;
      setTimeout(() => { this.profileMsg = ''; }, 3000);
    },

    // ========== Cash Flow ==========
    async onCashFlowTab() {
      this.tab = 'cashflow';
      await this.loadCashFlow();
    },
    async loadCashFlow() {
      try {
        const r = await executeJson({ function: 'getPlatformCashFlow' }, simitrack);
        if (r.data.success) this.cashFlow = r.data.data || {};
      } catch (e) { /* silent */ }
    },

    // ========== Accounting ==========
    invoiceBadge(s) {
      return ({ pending: 'st-badge-warning', paid: 'st-badge-success', overdue: 'st-badge-danger', cancelled: 'st-badge-info' })[s] || 'st-badge-info';
    },
    async onAccountingTab() {
      this.tab = 'accounting';
      await Promise.all([this.loadAccountingSummary(), this.loadInvoices()]);
    },
    async loadAccountingSummary() {
      try {
        const r = await executeJson({ function: 'getAccountingSummary' }, simitrack);
        if (r.data.success) this.acct = r.data.data || {};
      } catch (e) { /* silent */ }
    },
    async loadInvoices() {
      try {
        const r = await executeJson({ function: 'listAllSchoolInvoices', status: this.invFilter }, simitrack);
        if (r.data.success) this.invoices = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) { /* silent */ }
    },
    async runMonthEnd() {
      if (!confirm('Generate per-student-per-term invoices for EVERY active institution using their current term and configured rates?')) return;
      this.acctRunning = true; this.monthEndResult = '';
      try {
        const r = await executeJson({ function: 'generateTermInvoicesAll' }, simitrack);
        if (r.data.success) {
          const results = r.data.data || [];
          const created = results.filter(x => x.invoices_created).reduce((sum, x) => sum + (x.invoices_created || 0), 0);
          const schoolBundled = results.filter(x => x.mode === 'bundled_in_fees').length;
          const skipped = results.filter(x => x.skipped).length;
          this.monthEndResult = `Done: ${created} parent invoices created, ${schoolBundled} bundled-school invoices, ${skipped} skipped.`;
          await Promise.all([this.loadAccountingSummary(), this.loadInvoices(), this.loadDashboard()]);
        } else { alert(r.data.message || 'Failed'); }
      } catch (e) { alert('Connection error'); }
      this.acctRunning = false;
      setTimeout(() => { this.monthEndResult = ''; }, 8000);
    },
    openSettle(inv) {
      this.settleInv = inv;
      this.settleError = '';
      this.settleForm = { paymentMethod: 'mpesa', paidAmount: Number(inv.amount || 0), mpesaCode: '', notes: '', settleAllPending: false };
      this.showSettle = true;
    },
    async confirmSettle() {
      if (!this.settleInv) return;
      this.settleSaving = true; this.settleError = '';
      try {
        const params = { function: 'settleSchoolInvoice', invoiceId: this.settleInv.id, ...this.settleForm, settleAllPending: this.settleForm.settleAllPending ? 1 : 0 };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.showSettle = false;
          await Promise.all([this.loadAccountingSummary(), this.loadInvoices(), this.loadDashboard()]);
        } else { this.settleError = r.data.message || 'Failed'; }
      } catch (e) { this.settleError = 'Connection error'; }
      this.settleSaving = false;
    },

    // ========== Paybills (per-school) ==========
    async loadPaybills(institutionId) {
      this.paybillsLoading = true;
      this.paybills = [];
      try {
        const r = await executeJson({ function: 'adminListPaybills', institutionId }, simitrack);
        if (r.data.success) this.paybills = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) { /* silent */ }
      this.paybillsLoading = false;
    },
    openAddPaybill() {
      this.pbError = '';
      this.pbForm = { id: null, shortcode: '', type: 'paybill', environment: 'sandbox', label: 'M-Pesa', accountPrefix: '', purpose: 'all', consumerKey: '', consumerSecret: '', passkey: '', initiatorName: '', isPrimary: false, isActive: true };
      this.showPaybillForm = true;
    },
    openEditPaybill(pb) {
      this.pbError = '';
      this.pbForm = {
        id: pb.id,
        shortcode: pb.shortcode || '',
        type: pb.type || 'paybill',
        environment: pb.environment || 'sandbox',
        label: pb.label || 'M-Pesa',
        accountPrefix: pb.account_prefix || '',
        purpose: pb.purpose || 'all',
        consumerKey: '',         // blank = keep existing
        consumerSecret: '',
        passkey: '',
        initiatorName: '',
        isPrimary: pb.is_primary == 1,
        isActive: pb.is_active == 1,
      };
      this.showPaybillForm = true;
    },
    closePaybillForm() {
      this.showPaybillForm = false;
      this.pbError = '';
    },
    async savePaybill() {
      if (!this.active.institution) return;
      if (!this.pbForm.shortcode) { this.pbError = 'Shortcode is required'; return; }
      if (!this.pbForm.id && (!this.pbForm.consumerKey || !this.pbForm.consumerSecret)) { this.pbError = 'Consumer Key and Secret are required for new paybills'; return; }
      this.pbSaving = true;
      try {
        const fn = this.pbForm.id ? 'adminUpdatePaybill' : 'adminAddPaybill';
        const params = {
          function: fn,
          institutionId: this.active.institution.id,
          paybillId: this.pbForm.id || undefined,
          shortcode: this.pbForm.shortcode,
          type: this.pbForm.type,
          environment: this.pbForm.environment,
          label: this.pbForm.label,
          accountPrefix: this.pbForm.accountPrefix,
          purpose: this.pbForm.purpose,
          consumerKey: this.pbForm.consumerKey,
          consumerSecret: this.pbForm.consumerSecret,
          passkey: this.pbForm.passkey,
          initiatorName: this.pbForm.initiatorName,
          isPrimary: this.pbForm.isPrimary ? 1 : 0,
          isActive: this.pbForm.isActive ? 1 : 0,
        };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.showPaybillForm = false;
          await this.loadPaybills(this.active.institution.id);
        } else { this.pbError = r.data.message || 'Save failed'; }
      } catch (e) { this.pbError = 'Connection error'; }
      this.pbSaving = false;
    },
    async deletePaybill(pb) {
      if (!confirm(`Delete paybill ${pb.shortcode}? If payments reference it, you'll be told to deactivate instead.`)) return;
      pb._busy = true;
      try {
        const r = await executeJson({ function: 'adminDeletePaybill', institutionId: this.active.institution.id, paybillId: pb.id }, simitrack);
        if (r.data.success) { await this.loadPaybills(this.active.institution.id); }
        else { alert(r.data.message || 'Delete failed'); }
      } catch (e) { alert('Connection error'); }
      pb._busy = false;
    },
    async testConnection(pb) {
      pb._busy = true;
      try {
        const r = await executeJson({ function: 'adminTestPaybillConnection', institutionId: this.active.institution.id, paybillId: pb.id }, simitrack);
        if (r.data.success) alert(`✓ ${r.data.message}`);
        else alert(`✗ ${r.data.message || 'Connection failed'}`);
      } catch (e) { alert('Connection error'); }
      pb._busy = false;
    },
    openTestStk(pb) {
      this.testStkPaybill = pb;
      this.testStkError = '';
      this.testStkForm = { phone: sessionStorage.getItem('userPhone') || '', amount: 1 };
      this.resetTestStk();
      this.showTestStk = true;
    },
    resetTestStk() {
      this.stopTestStkPolling();
      this.testStkCheckoutId = null;
      this.testStkStatus = { stage: 'idle' };
      this.testStkError = '';
      this.testStkElapsed = 0;
    },
    closeTestStk() {
      this.stopTestStkPolling();
      this.showTestStk = false;
      this.resetTestStk();
    },
    stopTestStkPolling() {
      if (this.testStkPollHandle) { clearInterval(this.testStkPollHandle); this.testStkPollHandle = null; }
    },
    setTestStkStage(stage, payload) {
      const map = {
        sending:   { title: 'Sending STK push...',                        detail: 'Reaching Daraja',                                            icon: 'mdi-progress-clock',  iconColor: '#4FC3F7', titleColor: '#4FC3F7', style: 'background:rgba(46,134,222,.08);border:1px solid rgba(46,134,222,.3);' },
        pending:   { title: 'Waiting for the user to authorise…',         detail: 'Enter your M-Pesa PIN on the phone to confirm.',             icon: 'mdi-cellphone',       iconColor: '#F59E0B', titleColor: '#F59E0B', style: 'background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.3);' },
        paid:      { title: 'Payment received ✓',                         detail: 'The paybill is fully working — credentials, STK, callback.', icon: 'mdi-check-circle',    iconColor: '#10B981', titleColor: '#10B981', style: 'background:rgba(16,185,129,.08);border:1px solid rgba(16,185,129,.3);' },
        cancelled: { title: 'User cancelled the prompt',                  detail: 'No money moved. The paybill itself is fine.',                icon: 'mdi-close-circle',    iconColor: '#F59E0B', titleColor: '#F59E0B', style: 'background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.3);' },
        failed:    { title: 'Payment failed',                             detail: 'Check the result below for the Daraja reason.',              icon: 'mdi-alert-circle',    iconColor: '#EF4444', titleColor: '#EF4444', style: 'background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.3);' },
        timeout:   { title: 'Timed out — no response from M-Pesa',        detail: 'The user may have ignored the prompt. Try again.',           icon: 'mdi-clock-alert',     iconColor: '#F59E0B', titleColor: '#F59E0B', style: 'background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.3);' },
      };
      const base = map[stage] || { title: stage, detail: '', icon: 'mdi-information', iconColor: '#8BA4C4', titleColor: '#E8EDF4', style: 'background:#0A1628;border:1px solid #1A3A6A;' };
      this.testStkStatus = { stage, ...base, ...(payload || {}) };
    },
    async confirmTestStk() {
      if (!this.testStkPaybill) return;
      this.testStkError = '';
      if (!this.testStkForm.phone) { this.testStkError = 'Phone number required'; return; }
      this.testStkSending = true;
      this.setTestStkStage('sending');
      try {
        const r = await executeJson({
          function: 'adminTestPaybillStk',
          institutionId: this.active.institution.id,
          paybillId: this.testStkPaybill.id,
          phone: this.testStkForm.phone,
          amount: this.testStkForm.amount,
        }, simitrack);
        if (r.data.success) {
          const checkoutId = r.data.data && r.data.data.checkout_request_id;
          if (!checkoutId) {
            this.testStkError = 'STK sent but no CheckoutRequestID returned — cannot poll for status.';
            this.setTestStkStage('failed', { detail: 'Missing checkout id from Daraja response.' });
          } else {
            this.testStkCheckoutId = checkoutId;
            this.setTestStkStage('pending');
            this.testStkStartedAt = Date.now();
            this.testStkElapsed = 0;
            await this.loadPaybills(this.active.institution.id);
            this.startTestStkPolling();
          }
        } else {
          this.testStkError = r.data.message || 'STK failed';
          this.setTestStkStage('failed', { detail: r.data.message || 'Daraja rejected the request.' });
        }
      } catch (e) {
        this.testStkError = 'Connection error';
        this.setTestStkStage('failed', { detail: 'Connection error reaching the API.' });
      }
      this.testStkSending = false;
    },
    startTestStkPolling() {
      const TIMEOUT_MS = 90 * 1000;
      const INTERVAL_MS = 3000;
      this.stopTestStkPolling();
      this.testStkPollHandle = setInterval(async () => {
        const elapsed = Math.floor((Date.now() - this.testStkStartedAt) / 1000);
        this.testStkElapsed = elapsed;
        if (Date.now() - this.testStkStartedAt > TIMEOUT_MS) {
          this.stopTestStkPolling();
          this.setTestStkStage('timeout');
          return;
        }
        try {
          const r = await executeJson({
            function: 'adminCheckStkStatus',
            institutionId: this.active.institution.id,
            paybillId: this.testStkPaybill && this.testStkPaybill.id,
            checkoutRequestId: this.testStkCheckoutId,
          }, simitrack);
          if (r.data.success) {
            const d = r.data.data || {};
            const stage = d.stage;
            if (stage === 'paid') {
              this.stopTestStkPolling();
              this.setTestStkStage('paid', { detail: `Amount: KSh ${this.fNum(d.amount)} · Phone: ${d.phone || this.testStkForm.phone}`, mpesaReceipt: d.mpesa_receipt });
            } else if (stage === 'cancelled') {
              this.stopTestStkPolling();
              this.setTestStkStage('cancelled', { detail: d.result_desc || 'User cancelled at the M-Pesa prompt.' });
            } else if (stage === 'failed') {
              this.stopTestStkPolling();
              this.setTestStkStage('failed', { detail: d.result_desc || ('Result code: ' + (d.result_code || 'unknown')) });
            }
            // else still pending — keep polling
          }
        } catch (e) { /* swallow — keep polling until timeout */ }
      }, INTERVAL_MS);
    },
    openTestB2B(pb) {
      this.testB2BPaybill = pb;
      this.resetTestB2B();
      this.testB2BForm = {
        destinationShortcode: '',
        amount: 10,
        accountReference: 'TEST-' + (this.active.institution && this.active.institution.id),
        commandId: 'BusinessPayBill',
        remarks: 'Simi Trac B2B Test',
      };
      this.showTestB2B = true;
    },
    resetTestB2B() {
      this.stopTestB2BPolling();
      this.testB2BConversationId = null;
      this.testB2BStatus = { stage: 'idle' };
      this.testB2BError = '';
      this.testB2BElapsed = 0;
    },
    closeTestB2B() {
      this.stopTestB2BPolling();
      this.showTestB2B = false;
      this.resetTestB2B();
    },
    stopTestB2BPolling() {
      if (this.testB2BPollHandle) { clearInterval(this.testB2BPollHandle); this.testB2BPollHandle = null; }
    },
    setTestB2BStage(stage, payload) {
      const map = {
        sending:  { title: 'Sending B2B request to Daraja...',         detail: 'Authenticating + signing with security credential.',          icon: 'mdi-progress-clock',  iconColor: '#4FC3F7', titleColor: '#4FC3F7', style: 'background:rgba(46,134,222,.08);border:1px solid rgba(46,134,222,.3);' },
        pending:  { title: 'Daraja accepted — waiting for settlement', detail: 'Result is async. M-Pesa is processing the payout.',           icon: 'mdi-bank-transfer',   iconColor: '#A855F7', titleColor: '#A855F7', style: 'background:rgba(168,85,247,.08);border:1px solid rgba(168,85,247,.3);' },
        paid:     { title: 'B2B successful ✓',                          detail: 'Money transferred. Paybill is fully working for B2B payouts.', icon: 'mdi-check-circle',    iconColor: '#10B981', titleColor: '#10B981', style: 'background:rgba(16,185,129,.08);border:1px solid rgba(16,185,129,.3);' },
        failed:   { title: 'B2B failed',                                detail: 'Check the result below for the Daraja reason.',                icon: 'mdi-alert-circle',    iconColor: '#EF4444', titleColor: '#EF4444', style: 'background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.3);' },
        timeout:  { title: 'Timed out — no callback yet',               detail: 'Daraja accepted the request but the result is taking longer than usual.', icon: 'mdi-clock-alert', iconColor: '#F59E0B', titleColor: '#F59E0B', style: 'background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.3);' },
      };
      const base = map[stage] || { title: stage, detail: '', icon: 'mdi-information', iconColor: '#8BA4C4', titleColor: '#E8EDF4', style: 'background:#0A1628;border:1px solid #1A3A6A;' };
      this.testB2BStatus = { stage, ...base, ...(payload || {}) };
    },
    async confirmTestB2B() {
      if (!this.testB2BPaybill) return;
      this.testB2BError = '';
      if (!this.testB2BForm.destinationShortcode) { this.testB2BError = 'Destination shortcode required'; return; }
      this.testB2BSending = true;
      this.setTestB2BStage('sending');
      try {
        const r = await executeJson({
          function: 'adminTestPaybillB2B',
          institutionId: this.active.institution.id,
          paybillId: this.testB2BPaybill.id,
          ...this.testB2BForm,
        }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          const convId = d.conversation_id || d.originator_conversation_id;
          if (!convId) {
            this.testB2BError = 'Daraja accepted but no ConversationID — cannot poll status.';
            this.setTestB2BStage('failed', { detail: 'Missing ConversationID in Daraja response.' });
          } else {
            this.testB2BConversationId = convId;
            this.setTestB2BStage('pending', { conversationId: convId, detail: `Daraja accepted (${d.environment || 'production'}). Polling our callback log…` });
            this.testB2BStartedAt = Date.now();
            this.testB2BElapsed = 0;
            this.startTestB2BPolling();
          }
        } else {
          this.testB2BError = r.data.message || 'B2B failed';
          this.setTestB2BStage('failed', { detail: r.data.message || 'Daraja rejected the request.' });
        }
      } catch (e) {
        this.testB2BError = 'Connection error';
        this.setTestB2BStage('failed', { detail: 'Connection error reaching the API.' });
      }
      this.testB2BSending = false;
    },
    startTestB2BPolling() {
      const TIMEOUT_MS = 120 * 1000;
      const INTERVAL_MS = 4000;
      this.stopTestB2BPolling();
      this.testB2BPollHandle = setInterval(async () => {
        const elapsed = Math.floor((Date.now() - this.testB2BStartedAt) / 1000);
        this.testB2BElapsed = elapsed;
        if (Date.now() - this.testB2BStartedAt > TIMEOUT_MS) {
          this.stopTestB2BPolling();
          this.setTestB2BStage('timeout', { conversationId: this.testB2BConversationId });
          return;
        }
        try {
          const r = await executeJson({
            function: 'adminCheckB2BStatus',
            conversationId: this.testB2BConversationId,
          }, simitrack);
          if (r.data.success) {
            const d = r.data.data || {};
            if (d.stage === 'paid') {
              this.stopTestB2BPolling();
              this.setTestB2BStage('paid', {
                conversationId: this.testB2BConversationId,
                transactionId: d.transaction_id,
                detail: `Amount: KSh ${this.fNum(d.amount)} · Destination: ${d.destination || this.testB2BForm.destinationShortcode}`,
              });
            } else if (d.stage === 'failed') {
              this.stopTestB2BPolling();
              this.setTestB2BStage('failed', {
                conversationId: this.testB2BConversationId,
                detail: d.result_desc || ('Result code: ' + (d.result_code || 'unknown')),
              });
            }
            // else still pending — keep polling
          }
        } catch (e) { /* swallow — keep polling until timeout */ }
      }, INTERVAL_MS);
    },

    async activatePaybill(pb) {
      const verb = pb.callbacks_registered == 1 ? 'Re-register' : 'Activate';
      if (!confirm(`${verb} paybill ${pb.shortcode}? This registers C2B callback URLs with Daraja so M-Pesa can notify the platform when a payment lands.`)) return;
      pb._busy = true;
      try {
        const r = await executeJson({ function: 'adminRegisterPaybillCallbacks', institutionId: this.active.institution.id, paybillId: pb.id }, simitrack);
        if (r.data.success) {
          alert(`✓ ${r.data.message}`);
          await this.loadPaybills(this.active.institution.id);
        } else {
          alert(`✗ ${r.data.message || 'Activation failed'}`);
        }
      } catch (e) { alert('Connection error'); }
      pb._busy = false;
    },

    // ========== Onboard School ==========
    resetOnboard() {
      this.onboardSuccess = false;
      this.onboardError = '';
      this.onboardTempPassword = '';
      this.onboardAppliedPresets = [];
      this.onboardForm = { name: '', type: 'mixed', educationType: 'mixed_school', email: '', city: '', adminName: '', phone: '', password: '', billingMode: 'direct_parent', isFree: 1 };
    },
    closeOnboard() {
      this.showOnboard = false;
      this.resetOnboard();
    },
    async onboardSchool() {
      this.onboardError = '';
      if (!this.onboardForm.name) { this.onboardError = 'School name is required'; return; }
      if (!this.onboardForm.email) { this.onboardError = 'School email is required'; return; }
      if (!this.onboardForm.adminName) { this.onboardError = 'Super-admin name is required'; return; }
      if (!this.onboardForm.phone) { this.onboardError = 'Super-admin phone is required'; return; }
      this.onboardSaving = true;
      try {
        const r = await executeJson({ function: 'onboardInstitution', ...this.onboardForm }, simitrack);
        if (r.data.success) {
          this.onboardTempPassword = (r.data.data && r.data.data.tempPassword) || (this.onboardForm.password || '—');
          this.onboardAppliedPresets = (r.data.data && r.data.data.appliedPresets) || [];
          this.onboardSuccess = true;
          await this.loadDashboard();
        } else {
          this.onboardError = r.data.message || 'Failed to onboard';
        }
      } catch (e) { this.onboardError = 'Connection error'; }
      this.onboardSaving = false;
    },

    async revokeOwner(o) {
      if (!confirm(`Revoke platform owner access for ${o.name}? They will be deactivated.`)) return;
      try {
        const r = await executeJson({ function: 'revokePlatformOwner', userId: o.id }, simitrack);
        if (r.data.success) { await this.loadPlatformOwners(); }
        else { alert(r.data.message || 'Revoke failed'); }
      } catch (e) { alert('Connection error'); }
    },
    async openBilling(item) {
      this.showBilling = true;
      this.billingLoading = true;
      this.billingMsg = ''; this.billingError = '';
      this.profileMsg = '';
      this.active = { institution: item, profile: null, stats: null };
      this.profileForm = {
        name: item.name || '',
        type: item.type || 'mixed',
        email: item.email || '',
        phone: item.phone || '',
        city: item.city || '',
      };
      this.loadPaybills(item.id);
      this.loadSettlements(item.id);
      this.loadInvoices(item.id);
      try {
        const r = await executeJson({ function: 'getBillingProfile', institutionId: item.id }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.active = { institution: d.institution || item, profile: d.profile || {}, stats: d.stats || {} };
          const p = d.profile || {};
          this.form.isFree = Number(p.is_free ?? 1);
          this.form.attendanceBillingMode = p.attendance_billing_mode || 'direct_parent';
          this.form.attendanceRate = Number(p.attendance_rate || 1000);
          this.form.billingFrequency = p.billing_frequency || 'per_term';
          this.form.posCommissionPct = Number(p.pos_commission_pct || 5);
          this.form.cardIssuanceFee = Number(p.card_issuance_fee || 500);
          this.form.cardValidityMonths = Number(p.card_validity_months || 12);
          this.form.freeUntil = p.free_until || '';
          this.form.gracePeriodDays = Number(p.grace_period_days || 7);
          this.form.billingStatus = (d.institution && d.institution.billing_status) || 'free';
        } else {
          this.billingError = r.data.message || 'Failed to load profile';
        }
      } catch (e) { this.billingError = 'Connection error'; }
      this.billingLoading = false;
    },
    closeBilling() {
      this.showBilling = false;
      this.active = { institution: null, profile: null, stats: null };
      this.settle = { loading: false, list: [], accrued: 0, settled: 0, outstanding: 0 };
      this.inv = { loading: false, list: [], unbilled_spans: [] };
    },

    // ============ PLATFORM INVOICES ============
    async onInvoicesTab() {
      this.tab = 'invoices';
      await this.loadAllInvoices();
    },
    async loadAllInvoices() {
      this.allInv.loading = true;
      try {
        const r = await executeJson({
          function: 'listAllPlatformInvoices',
          status: this.allInv.status,
          search: this.allInv.search,
        }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.allInv.list = d.invoices || [];
          this.allInv.totals = d.totals || { count: 0, amount: 0, paid: 0, outstanding: 0 };
        }
      } catch (e) { /* silent */ }
      this.allInv.loading = false;
    },
    setAllInvStatus(s) {
      this.allInv.status = s;
      this.loadAllInvoices();
    },
    onAllInvSearch() {
      // Debounce so each keystroke doesn't fire a request
      if (this.allInv._searchTimer) clearTimeout(this.allInv._searchTimer);
      this.allInv._searchTimer = setTimeout(() => this.loadAllInvoices(), 350);
    },
    async printInvoice(i) {
      try {
        const r = await executeJson({ function: 'getPlatformInvoiceDetails', invoiceId: i.id }, simitrack);
        if (r.data.success) printPlatformInvoice(r.data.data);
        else alert(r.data.message || 'Could not load invoice');
      } catch (e) { alert('Connection error'); }
    },
    openBillingForInvoice(i) {
      // Find the institution row and open the billing modal — same handler the institutions table uses
      const inst = (this.institutions || []).find(x => String(x.id) === String(i.institution_id))
        || { id: i.institution_id, name: i.institution_name };
      this.openBilling(inst);
    },
    invStatusBadge(status) {
      return status === 'paid' ? 'st-badge-success'
           : status === 'partial' ? 'st-badge-warning'
           : status === 'void' ? 'st-badge-danger'
           : 'st-badge-info';
    },
    async loadInvoices(institutionId) {
      this.inv.loading = true;
      try {
        const r = await executeJson({ function: 'listPlatformInvoices', institutionId }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.inv.list = d.invoices || [];
          this.inv.unbilled_spans = d.unbilled_spans || [];
        }
      } catch (e) { /* silent */ }
      this.inv.loading = false;
    },
    openGenerateInvoice() {
      if (!this.active.institution) return;
      // Default range: first unbilled span if any, else this month
      let start = '', end = '';
      if (this.inv.unbilled_spans && this.inv.unbilled_spans.length) {
        const s = this.inv.unbilled_spans[0];
        start = s.start.substr(0, 10);
        end = s.end.substr(0, 10);
      } else {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        start = first.toISOString().substr(0, 10);
        end = now.toISOString().substr(0, 10);
      }
      this.genForm = {
        institutionId: this.active.institution.id,
        institutionName: this.active.institution.name,
        start, end, notes: '',
        preview: null, previewing: false, saving: false, error: '', success: '',
      };
      this.showGenInvoice = true;
      this.runPreview();
    },
    closeGenInvoice() {
      if (this.genForm.saving) return;
      this.showGenInvoice = false;
    },
    prefillInvoiceRange(span) {
      // Click a gap chip → pre-fills the dates and pre-runs the preview
      this.genForm.start = span.start.substr(0, 10);
      this.genForm.end = span.end.substr(0, 10);
      if (!this.showGenInvoice) this.openGenerateInvoice();
      else this.runPreview();
    },
    async runPreview() {
      const f = this.genForm;
      f.error = ''; f.preview = null;
      if (!f.start || !f.end) return;
      if (f.start > f.end) { f.error = 'Start date is after end date'; return; }
      f.previewing = true;
      try {
        const r = await executeJson({
          function: 'previewPlatformInvoice',
          institutionId: f.institutionId,
          periodStart: f.start, periodEnd: f.end,
        }, simitrack);
        if (r.data.success) f.preview = r.data.data;
        else f.error = r.data.message || 'Preview failed';
      } catch (e) { f.error = 'Connection error'; }
      f.previewing = false;
    },
    async confirmGenerate() {
      const f = this.genForm;
      f.error = ''; f.success = '';
      if (!f.start || !f.end) { f.error = 'Pick a date range'; return; }
      if (!f.preview || f.preview.will_include_count === 0) { f.error = 'Nothing to invoice in this range'; return; }
      // If skipping, show a confirmation first
      if (f.preview.will_skip_count > 0) {
        const overlapList = (f.preview.overlap_invoices || []).map(o => o.invoice_no).join(', ');
        if (!confirm(`${f.preview.will_skip_count} line(s) (KSh ${this.fNum(f.preview.will_skip_amount)}) are already on ${overlapList || 'another invoice'}.\n\nThe new invoice will only include the ${f.preview.will_include_count} unbilled lines (KSh ${this.fNum(f.preview.will_include_amount)}).\n\nProceed?`)) return;
      }
      f.saving = true;
      try {
        const r = await executeJson({
          function: 'generatePlatformInvoice',
          institutionId: f.institutionId,
          periodStart: f.start, periodEnd: f.end,
          notes: f.notes,
        }, simitrack);
        if (r.data.success) {
          f.success = r.data.message || 'Invoice issued';
          await this.loadInvoices(f.institutionId);
          await this.loadDashboard();
          setTimeout(() => { this.showGenInvoice = false; f.success = ''; }, 1800);
        } else {
          f.error = r.data.message || 'Generation failed';
        }
      } catch (e) { f.error = 'Connection error'; }
      f.saving = false;
    },
    payAgainstInvoice(i) {
      // Pre-fill the Record Settlement modal with this invoice's outstanding
      if (!this.active.institution) return;
      this.openSettlement(this.active.institution);
      const f = this.settleForm;
      f.amount = Number(i.outstanding);
      f.notes = `Payment against ${i.invoice_no}`;
      // Stash invoice_no on the form so saveSettlement can include it
      f.invoiceNo = i.invoice_no;
    },
    async voidInvoice(i) {
      if (!confirm(`Void invoice ${i.invoice_no}?\n\nThis un-claims its ${i.line_count} line(s) so they go back to "unbilled" and can be re-invoiced. Cannot void if any payment is allocated.`)) return;
      const reason = prompt('Reason (optional):') || '';
      try {
        const r = await executeJson({ function: 'voidPlatformInvoice', invoiceId: i.id, reason }, simitrack);
        if (r.data.success) {
          if (this.active.institution) await this.loadInvoices(this.active.institution.id);
          await this.loadDashboard();
        } else {
          alert('✗ ' + (r.data.message || 'Void failed'));
        }
      } catch (e) { alert('✗ Connection error'); }
    },
    async togglePlatformPaybill(pb) {
      const turningOn = pb.is_platform_paybill != 1;
      if (turningOn && !confirm(`Mark paybill ${pb.shortcode} as the platform's collection account?\n\nFuture STK pushes for invoices will charge to this paybill, and incoming C2B payments with PINV-#### account refs will auto-settle.`)) return;
      try {
        const r = await executeJson({
          function: 'setPlatformPaybill',
          paybillId: pb.id,
          isPlatform: turningOn ? 1 : 0,
        }, simitrack);
        if (r.data.success) {
          if (this.active.institution) await this.loadPaybills(this.active.institution.id);
        } else {
          alert('✗ ' + (r.data.message || 'Failed'));
        }
      } catch (e) { alert('✗ Connection error'); }
    },

    // ============ SETTLEMENTS ============
    async loadSettlements(institutionId) {
      this.settle.loading = true;
      try {
        const r = await executeJson({ function: 'listPlatformSettlements', institutionId }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          this.settle.list = d.settlements || [];
          this.settle.accrued = Number(d.accrued || 0);
          this.settle.settled = Number(d.settled || 0);
          this.settle.outstanding = Number(d.outstanding || 0);
        }
      } catch (e) { /* silent — modal still useful without history */ }
      this.settle.loading = false;
    },
    async toggleInstitutionActive(institution) {
      if (!institution) return;
      const isActiveNow = Number(institution.is_active) === 1;
      const action = isActiveNow ? 'suspend' : 'reactivate';
      let reason = '';
      if (isActiveNow) {
        const confirmMsg = `Suspend "${institution.name}"?\n\nThis will:\n• Block all staff and parent logins\n• End all active sessions\n• Notify school admins by SMS and email\n\nUsed for non-paying clients. Reactivate any time from this same button.`;
        if (!window.confirm(confirmMsg)) return;
        reason = window.prompt('Optional note for the school (included in the email). Leave blank if none:', '') || '';
      } else {
        if (!window.confirm(`Re-activate "${institution.name}"?\n\nLogins will be restored immediately and admins will be notified.`)) return;
      }
      try {
        const res = await executeJson({
          function: 'setInstitutionActive',
          institutionId: institution.id,
          isActive: isActiveNow ? 0 : 1,
          reason: reason,
        }, simitrack);
        if (res.data.success) {
          institution.is_active = isActiveNow ? 0 : 1;
          alert(res.data.message || `Institution ${action}d`);
          this.loadDashboard();
        } else {
          alert(res.data.message || `Failed to ${action} institution`);
        }
      } catch (e) {
        alert(e.response?.data?.message || e.message || `Failed to ${action} institution`);
      }
    },
    openSettlement(institution) {
      if (!institution) return;
      // Pre-fetch outstanding so the modal shows the right "currently owed" line.
      // If invoked from the Billing modal we already have it; otherwise fetch fresh.
      const cached = this.settle && this.settle.outstanding && this.active.institution && this.active.institution.id === institution.id
        ? this.settle.outstanding
        : Number(institution.platform_outstanding || 0);
      const now = new Date();
      const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      this.settleForm = {
        institutionId: institution.id,
        institutionName: institution.name,
        outstanding: cached,
        amount: null,
        mpesaCode: '',
        paidAt: localIso,
        source: 'manual',
        notes: '',
        invoiceNo: '',                                  // populated by payAgainstInvoice() when settlement is for a specific PINV
        saving: false,
        error: '',
        success: '',
      };
      this.showRecordSettlement = true;
      // Refresh outstanding asynchronously (don't block the modal)
      if (!this.active.institution || this.active.institution.id !== institution.id) {
        executeJson({ function: 'listPlatformSettlements', institutionId: institution.id }, simitrack)
          .then(r => { if (r.data.success && r.data.data) this.settleForm.outstanding = Number(r.data.data.outstanding || 0); })
          .catch(() => {});
      }
    },
    closeSettlement() {
      if (this.settleForm.saving) return;
      this.showRecordSettlement = false;
    },
    async saveSettlement() {
      const f = this.settleForm;
      f.error = ''; f.success = '';
      if (!f.institutionId || !f.amount || f.amount <= 0) { f.error = 'Amount is required'; return; }
      f.saving = true;
      try {
        const params = {
          function: 'recordPlatformSettlement',
          institutionId: f.institutionId,
          amount: f.amount,
          mpesaCode: (f.mpesaCode || '').trim().toUpperCase(),
          paidAt: f.paidAt ? f.paidAt.replace('T', ' ') + ':00' : '',
          source: f.source || 'manual',
          notes: f.notes || '',
          invoiceNo: f.invoiceNo || '',                // optional — prefer this invoice's rows during allocation
        };
        const r = await executeJson(params, simitrack);
        if (r.data.success) {
          f.success = r.data.message || 'Recorded';
          // Refresh: settlement list + invoices (allocation may have flipped paid status) + dashboard
          if (this.active.institution && this.active.institution.id == f.institutionId) {
            await this.loadSettlements(f.institutionId);
            await this.loadInvoices(f.institutionId);
          }
          await this.loadDashboard();
          setTimeout(() => { this.showRecordSettlement = false; f.success = ''; }, 1500);
        } else {
          f.error = r.data.message || 'Save failed';
        }
      } catch (e) { f.error = 'Connection error'; }
      f.saving = false;
    },
    async reverseSettlement(s) {
      if (!s || !s.id) return;
      const reason = prompt(`Reverse this settlement of KSh ${this.fNum(s.amount)}?\n\nThis un-allocates the platform_revenue rows it covered — outstanding will go back up. Reason (optional):`);
      if (reason === null) return;   // cancelled
      try {
        const r = await executeJson({ function: 'reversePlatformSettlement', settlementId: s.id, reason }, simitrack);
        if (r.data.success) {
          if (this.active.institution) await this.loadSettlements(this.active.institution.id);
          await this.loadDashboard();
        } else {
          alert('✗ ' + (r.data.message || 'Reversal failed'));
        }
      } catch (e) { alert('✗ Connection error'); }
    },
    async saveBilling() {
      if (!this.active.institution) return;
      this.saving = true; this.billingMsg = ''; this.billingError = '';
      try {
        const params = { function: 'updateBillingProfile', institutionId: this.active.institution.id, ...this.form };
        const r = await executeJson(params, simitrack);
        if (r.data.success) { this.billingMsg = 'Rates saved'; await this.loadDashboard(); }
        else { this.billingError = r.data.message || 'Save failed'; }
      } catch (e) { this.billingError = 'Connection error'; }
      this.saving = false;
      setTimeout(() => { this.billingMsg = ''; }, 3000);
    },
    async generateInvoices() {
      if (!this.active.institution) return;
      if (!confirm('Generate per-student platform invoices for the CURRENT term using these rates?')) return;
      this.saving = true; this.billingMsg = ''; this.billingError = '';
      try {
        const r = await executeJson({ function: 'generateTermInvoices', institutionId: this.active.institution.id }, simitrack);
        if (r.data.success) {
          const d = r.data.data || {};
          if (d.skipped) {
            this.billingMsg = 'Skipped: ' + (d.reason || 'unknown');
          } else if (d.mode === 'direct_parent') {
            this.billingMsg = `Created ${d.invoices_created} parent invoices for ${d.students} students at KSh ${d.rate} each.`;
          } else if (d.mode === 'bundled_in_fees') {
            this.billingMsg = `School invoice ${d.invoice_no || ''}: ${d.students} students × rate = KSh ${this.fNum(d.amount)}.`;
          } else {
            this.billingMsg = r.data.message || 'Done';
          }
          this.openBilling(this.active.institution);
          this.loadDashboard();
        } else { this.billingError = r.data.message || 'Failed'; }
      } catch (e) { this.billingError = 'Connection error'; }
      this.saving = false;
      setTimeout(() => { this.billingMsg = ''; }, 6000);
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.st-stat-row{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:14px;}
.st-stat{background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;padding:14px 18px;flex:1;min-width:160px;}
.st-stat-num{font-size:22px;font-weight:800;color:#4FC3F7;}
.st-stat-lbl{font-size:11px;color:#8BA4C4;margin-top:2px;}
.st-tabs{display:flex;flex-wrap:wrap;gap:6px;margin:18px 0 14px;}
.st-tab{background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:50px;padding:7px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-tab.active,.st-tab:hover{background:rgba(46,134,222,.15);border-color:#2E86DE;color:#4FC3F7;}

/* Status filter pills (used on the All Invoices tab) */
.st-pill{background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:50px;padding:5px 14px;font-size:12px;font-weight:600;cursor:pointer;transition:all .15s;display:inline-flex;align-items:center;gap:6px;}
.st-pill:hover{border-color:#4FC3F7;color:#E8EDF4;}
.st-pill.active{background:rgba(46,134,222,.18);border-color:#4FC3F7;color:#4FC3F7;}
.st-pill.warn.active{background:rgba(245,158,11,.18);border-color:#F59E0B;color:#F59E0B;}
.st-pill.ok.active{background:rgba(16,185,129,.18);border-color:#10B981;color:#10B981;}
.st-pill.danger.active{background:rgba(239,68,68,.18);border-color:#EF4444;color:#EF4444;}
.st-pill-num{background:rgba(46,134,222,.2);color:#4FC3F7;border-radius:10px;padding:1px 7px;font-size:10px;font-weight:700;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;white-space:nowrap;}
.st-table td{color:#E8EDF4;font-size:13px;padding:12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.05);}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;}
.st-btn{border:none;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-btn-sm{padding:5px 12px;font-size:12px;}
.st-btn-blue{background:rgba(46,134,222,.15);color:#4FC3F7;border:1px solid rgba(46,134,222,.3);}
.st-btn-green{background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.3);}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE 0%,#4FC3F7 100%);color:#fff;border:none;border-radius:10px;padding:10px 22px;font-weight:700;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}
.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;}
.st-modal{background:#0F2347;border:1px solid #1A3A6A;border-radius:16px;padding:28px;width:100%;max-height:90vh;overflow-y:auto;}
.st-modal-title{color:#E8EDF4;font-size:18px;font-weight:700;margin-bottom:20px;display:flex;align-items:center;justify-content:space-between;}
.st-modal-close{color:#8BA4C4;cursor:pointer;font-size:22px;background:none;border:none;}
.st-label{color:#8BA4C4;font-size:13px;font-weight:600;margin-bottom:6px;display:block;}
.st-input,.st-select{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;}
.st-input:focus,.st-select:focus{border-color:#2E86DE;}
.st-search{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:9px 14px 9px 36px;color:#E8EDF4;font-size:14px;outline:none;width:100%;max-width:300px;}
.st-search-wrap{position:relative;}.st-search-wrap i{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8BA4C4;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;margin-bottom:16px;}
.st-alert-danger{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.2);color:#FF9999;}
.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#10B981;}
</style>
